/**
 * RTK Query API slice for HellocashflowAdmin.
 *
 * @remarks
 * This module centralizes:
 * - auth token injection (Firebase ID token via `MemoizedAuth`)
 * - retry/backoff + token refresh on 401
 * - endpoint definitions and cache invalidation tags
 * - small response normalizers/coercers for admin UI types
 *
 * Demo Access note:
 * When `VITE_ADMIN_DEMO_ACCESS_API_MODE=stub`, only the `/admin/demo-access` endpoints are handled via a
 * localStorage-backed stub store. All other endpoints remain live.
 *
 * Note: Network/auth concerns live in `src/store/api/baseQueryWithAuth.ts`.
 */
import type { Dispatch } from "@reduxjs/toolkit";
import { createApi } from "@reduxjs/toolkit/query/react";

import type { AdminV2AutomationFoundationPayload } from "./adminV2AutomationTypes";
import { buildAdminV2CrmUserEndpoints } from "./adminV2CrmUserEndpoints";
import {
  optionalMutationBody,
  optionalQueryParams,
  providesList,
} from "./apiEndpointHelpers";
import { enhancedBaseQuery } from "./baseQueryWithAuth";
import {
  hasCrmLinkedStateClause,
  hasCrmLinkedStateFilter,
} from "./crmLinkedStateFilterParam";
import { buildAdminV2AssistantEndpoints } from "./endpoints/adminV2AssistantEndpoints";
import { buildAdminV2AutomationBindingEndpoints } from "./endpoints/adminV2AutomationBindingEndpoints";
import { buildAdminV2GrowthEndpoints } from "./endpoints/adminV2GrowthEndpoints";
import { buildAdminV2LearningEndpoints } from "./endpoints/adminV2LearningEndpoints";
import {
  buildAdminV2LiveAlertCountEndpoints,
  requestFreshLiveAlertsCount,
} from "./endpoints/adminV2LiveAlertCountEndpoints";
import { buildAdminV2MessagingEndpoints } from "./endpoints/adminV2MessagingEndpoints";
import { buildAdminV2RevenueEndpoints } from "./endpoints/adminV2RevenueEndpoints";
import { buildAdminV2SupportConversationEndpoints } from "./endpoints/adminV2SupportConversationEndpoints";
import { buildAdminV2SupportPreviewKeepListEndpoints } from "./endpoints/adminV2SupportPreviewKeepListEndpoints";
import { buildAdminV2SupportTestResetEndpoints } from "./endpoints/adminV2SupportTestResetEndpoints";
import { getLiveAlertRelatedRecordTags } from "./liveAlertRelatedRecordTags";
import { getRecordActivityTags } from "./recordActivityTags";
import type { AdminMetricsResponse } from "../../types/adminMetrics";
import type {
  AdminPartnerDefaultOrganisationResponse,
  AdminPartnerDetail,
  AdminPartnerEntitlementsResponse,
  AdminPartnerSummary,
  AdminPartnerUnlinkResponse,
} from "../../types/adminPartners";
import type {
  AdminStatisticsResponse,
  IndustryBreakdownItem,
} from "../../types/adminStatistics";
import type {
  AdminSubscriptionAction,
  AdminSubscriptionLocalSnapshot,
  CreateAuditRunResponse,
  ExecuteAdminSubscriptionActionResult,
  RebuildSnapshotsResponse,
  ReconcileFromStripeResponse,
  StripeDiscountsResponse,
  StripeInvoicePreviewSummary,
  StripeSubscriptionSummary,
  SubscriptionAuditRun,
} from "../../types/adminSubscriptions";
import type {
  AdminV2ActivationReportResponse,
  AdminV2AddLiveAlertNoteRequest,
  AdminV2AiInsightsQualityResponse,
  AdminV2AnalyticsPipelineHealthResponse,
  AdminV2ApprovalsResponse,
  AdminV2ApprovalStatus,
  AdminV2AutomationAudienceEntityType,
  AdminV2AutomationCatalogueResponse,
  AdminV2AutomationConfigurationState,
  AdminV2AutomationDetailResponse,
  AdminV2AutomationGlobalRulesResponse,
  AdminV2AutomationGraphSchema,
  AdminV2AutomationOutputChannel,
  AdminV2AutomationPublishResponse,
  AdminV2AutomationRolloutMode,
  AdminV2AutomationRunDetailResponse,
  AdminV2AutomationRunsResponse,
  AdminV2AutomationRunStatus,
  AdminV2AutomationsListResponse,
  AdminV2AutomationStatus,
  AdminV2AutomationType,
  AdminV2AutomationWorkflowInstancesResponse,
  AdminV2AutomationWorkflowInstanceState,
  AdminV2CreateManualLiveAlertRequest,
  AdminV2CrmBillingResponsibility,
  AdminV2CrmBusinessesListResponse,
  AdminV2CrmBusinessRecordResponse,
  AdminV2CrmDuplicateCandidateListResponse,
  AdminV2CrmFilterSuggestionsResponse,
  AdminV2CrmFirmClientRelationshipRecordResponse,
  AdminV2CrmFirmClientRelationshipsListResponse,
  AdminV2CrmFirmRecordResponse,
  AdminV2CrmFirmsListResponse,
  AdminV2CrmIdentityReviewListResponse,
  AdminV2CrmImportRunResponse,
  AdminV2CrmOrganisationAccountingConnectionRepairPreviewResponse,
  AdminV2CrmOrganisationAccountingConnectionRepairResponse,
  AdminV2CrmOrganisationCancelSubscriptionTransferResponse,
  AdminV2CrmOrganisationConnectionHistoryResponse,
  AdminV2CrmOrganisationDisconnectPreviewResponse,
  AdminV2CrmOrganisationDisconnectResponse,
  AdminV2CrmOrganisationPendingSubscriptionTransferResponse,
  AdminV2CrmOrganisationsListResponse,
  AdminV2CrmPeopleBulkLifecycleStageResponse,
  AdminV2CrmPeopleListResponse,
  AdminV2CrmPeopleOwnerAssignmentResponse,
  AdminV2CrmPersonArchiveResponse,
  AdminV2CrmPersonMergePreviewResponse,
  AdminV2CrmPersonMergeResponse,
  AdminV2CrmPersonRecordResponse,
  AdminV2CrmPersonRestoreResponse,
  AdminV2CrmRelationshipStatus,
  AdminV2CrmStaffUsersResponse,
  AdminV2CrmSupportStateMatrixResponse,
  AdminV2CrmTimelineCategory,
  AdminV2CrmTimelineResponse,
  AdminV2EntitlementsResponse,
  AdminV2EntitlementUpsertResponse,
  AdminV2EventTaxonomyResponse,
  AdminV2FeatureAccessEnvironment,
  AdminV2FeatureAccessOrganisationsResponse,
  AdminV2FeatureAccessSummaryResponse,
  AdminV2FeatureFlagsResponse,
  AdminV2FeatureFlagUpsertResponse,
  AdminV2FeatureReadinessResponse,
  AdminV2HelpArticleDetailResponse,
  AdminV2HelpArticleSearchResponse,
  AdminV2HelpArticlesListResponse,
  AdminV2HelpArticleStatus,
  AdminV2HelpArticleUpdateResponse,
  AdminV2HelpCollectionRecordResponse,
  AdminV2HelpCollectionsListResponse,
  AdminV2HelpCollectionUpdateResponse,
  AdminV2HelpFeedbackListResponse,
  AdminV2HelpFeedbackRecordResponse,
  AdminV2HelpSearchLogsResponse,
  AdminV2IncidentOrganisationsResponse,
  AdminV2IntegrationConnectionHealthResponse,
  AdminV2IntegrationConnectionResponse,
  AdminV2IntegrationConnectionStatus,
  AdminV2IntegrationsResponse,
  AdminV2InteractionElementsResponse,
  AdminV2InteractionHeatmapGridResponse,
  AdminV2InteractionPipelineHealthResponse,
  AdminV2InteractionUnknownElementsResponse,
  AdminV2IssueReport,
  AdminV2IssueReportAction,
  AdminV2IssueReportBugSuggestionResult,
  AdminV2IssueReportBusinessRulesResponse,
  AdminV2IssueReportCloseoutInput,
  AdminV2IssueReportConvertToBugResponse,
  AdminV2IssueReportDeleteResponse,
  AdminV2IssueReportDeliveryEvidenceInput,
  AdminV2IssueReportDeliveryMilestone,
  AdminV2IssueReportDetailResponse,
  AdminV2IssueReportFollowUpAnswer,
  AdminV2IssueReportMoveToFeatureRequestsResponse,
  AdminV2IssueReportPriority,
  AdminV2IssueReportSite,
  AdminV2IssueReportsListResponse,
  AdminV2IssueReportStatus,
  AdminV2IssueReportValidationResult,
  AdminV2JobCancelResponse,
  AdminV2JobResponse,
  AdminV2JobsResponse,
  JsonValue as AdminV2JsonValue,
  AdminV2LiveAlertDetailResponse,
  AdminV2LiveAlertMutationResponse,
  AdminV2LiveAlertPriority,
  AdminV2LiveAlertsListResponse,
  AdminV2LiveAlertSort,
  AdminV2LiveAlertSource,
  AdminV2LiveAlertStaffUsersResponse,
  AdminV2LiveAlertStatus,
  AdminV2MeResponse,
  AdminV2OnboardingFunnelResponse,
  AdminV2OpenAiUsageResponse,
  AdminV2OpsDeliverabilityHealthResponse,
  AdminV2OpsIngestFailuresResponse,
  AdminV2OpsSupportBacklogResponse,
  AdminV2OpsWhatNeedsAttentionResponse,
  AdminV2OrgSyncSearchResponse,
  AdminV2PatchEventTaxonomyResponse,
  AdminV2PlatformStatusResponse,
  AdminV2PosthogDashboardsResponse,
  AdminV2ProductEventTaxonomyStatus,
  AdminV2ProfilePhotoUploadResponse,
  AdminV2RbacPermissionsResponse,
  AdminV2RbacRolesResponse,
  AdminV2RbacStaffUsersResponse,
  AdminV2RecordLiveAlertQuickActionRequest,
  AdminV2RecordLiveAlertQuickActionResponse,
  AdminV2ReleaseReviewCurrentResponse,
  AdminV2ReleaseReviewDetailResponse,
  AdminV2ReleaseReviewHistoryResponse,
  AdminV2ReleaseReviewItemStatus,
  AdminV2SafeAuditLogResponse,
  AdminV2SageBetaInviteMutationResponse,
  AdminV2SageBetaInviteRevokeResponse,
  AdminV2SageBetaInvitesResponse,
  AdminV2SalesDealEventMutationResponse,
  AdminV2SalesDealEventsResponse,
  AdminV2SalesDealMutationResponse,
  AdminV2SalesDealsResponse,
  AdminV2SalesLeadScoresResponse,
  AdminV2SalesMemberScoresResponse,
  AdminV2SalesQueueRole,
  AdminV2SalesRecomputeScoresResponse,
  AdminV2SalesReportingSummaryResponse,
  AdminV2SalesScoreBand,
  AdminV2SalesStage,
  AdminV2SalesWorkQueueResponse,
  AdminV2SearchResponse,
  AdminV2UpdateLiveAlertRequest,
} from "../../types/adminV2";
import type { AdminV2CrmOrganisationFeatureRequestsResponse } from "../../types/adminV2/crmOrganisationFeatureRequests";
import type {
  AdminV2IssueReportMasterLinkMutationResponse,
  AdminV2IssueReportMasterLinkRequest,
  AdminV2IssueReportMasterLinkResponse,
} from "../../types/adminV2/issueReportMasterLinks";
import type { JsonValue } from "../../types/bugTracker";
import type {
  CashflowDataQualityFlag,
  RawCashflowDataQualityFlag,
} from "../../types/CashflowDataQuality";
import type {
  AdminDocsSearchResponse,
  AdminDocsShareLinksResponse,
  CreateDocsShareLinkRequest,
  CreateDocsShareLinkResponse,
  DocsShareStatusFilter,
  RevokeDocsShareLinkResponse,
} from "../../types/docsShareLinks";
import type {
  AdminBulkEmailJobsResponse,
  AdminQuickbooksTrialsResponse,
  AdminSpoofSessionsResponse,
  AdminUserWithClaimsResponse,
  AuthProvider,
} from "../../types/helloCashflowDomain";
import { OrgSubscriptionStatus } from "../../types/helloCashflowDomain";
import type { Log } from "../../types/Logs";
import type {
  Organisation,
  OrganisationUsageActivityStatus,
  OrganisationUser,
} from "../../types/organisation";
import type { StripeSubscriptionObjectsResponse } from "../../types/stripeSubscriptionObjects";
import type {
  CheckoutSessionResponse,
  CreateCheckoutSessionRequest,
  CreatePortalSessionRequest,
  PortalSessionResponse,
  ProductOnboardingRequest,
  ProductWithPrices,
  StripeApiResponse,
  StripePrice,
  StripeProduct,
  StripeSubscription,
  SubscriptionStatisticsApiResponse,
} from "../../types/subscription";
import type {
  TranscriptIntakeCancelResponse,
  TranscriptIntakeCreateRequest,
  TranscriptIntakeCreateResponse,
  TranscriptIntakeJobResponse,
  TranscriptIntakeProcessRequest,
  TranscriptIntakeProcessResponse,
} from "../../types/transcriptIntake";
import type {
  UsageAnalyticsFilters,
  UsageAnalyticsResponse,
} from "../../types/usageAnalytics";
import type { AllUser, FirebaseUser } from "../../types/Users";

import type {
  AdminV2RecordActivityQuery,
  AdminV2RecordActivityResponse,
} from "@/types/adminV2";
import type { AdminV2SharedSegmentSelection } from "@/types/adminV2SharedSegments";
import type {
  CustomEmailCap,
  CustomEmailCapInput,
} from "@/vnext/routes/growth/automations/AutomationCustomRulesSection";

export type AdminOrganisationUserSummary = OrganisationUser;
export type AdminOrganisationSummary = Organisation;

export interface RemoveOrganisationUserRequest {
  organisationId: string;
  userId: string;
}

export interface RemoveOrganisationUserResponse {
  removedCount: number;
}

type RawLog = Omit<Log, "timestamp"> & { timestamp: string | number | Date };
/** Backend log feed request; `organisationId` scopes it to one org. */
export interface GetLogsArgs {
  pageSize?: number;
  organisationId?: string;
  syncId?: string;
}

const coerceNumber = (candidate: unknown, fallback = 0) => {
  if (typeof candidate === "number" && Number.isFinite(candidate)) {
    return candidate;
  }

  if (typeof candidate === "string") {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return fallback;
};

// Demo Access Types
export interface DemoAccessAssignment {
  assignmentKey?: string;
  firebaseId: string;
  email: string;
  displayName: string;
  demoAccessEnabled: boolean;
  grantedBy: string | null;
  updatedAt: string | null;
}

export interface GrantDemoAccessRequest {
  firebaseId?: string;
  email?: string;
}

export type GrantDemoAccessResponse = DemoAccessAssignment;

export interface RevokeDemoAccessRequest {
  firebaseId: string;
}

const toAdminUserSummary = (user: AllUser) => ({
  id: user.id,
  firebaseId: user.firebaseId,
  email: user.email ?? "",
  displayName: user.displayName ?? undefined,
  activity_status: user.activity_status,
  lastLoginAt: user.last_login_at,
  loginCount: user.login_count ?? 0,
  signupDate: user.signup_date,
  usageActiveMinutesLast7Days: user.usage_active_minutes_last_7_days ?? 0,
  usageLastActiveDate: user.usage_last_active_date ?? null,
  organisation_name: user.organisation_name ?? "",
  organisation_type: user.organisation_type ?? "",
  subscription_status: Number(user.subscriptionStatus ?? 0),
  authProviders: user.authProviders,
  providerConnections: user.providerConnections,
});

const toOrganisationUser = (candidate: unknown): OrganisationUser | null => {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const raw = candidate as Record<string, unknown>;
  const id = typeof raw.id === "string" ? raw.id : null;
  const firebaseId = typeof raw.firebaseId === "string" ? raw.firebaseId : null;

  if (!id || !firebaseId) {
    return null;
  }

  const firebaseUser =
    raw.firebaseUser && typeof raw.firebaseUser === "object"
      ? (raw.firebaseUser as FirebaseUser)
      : undefined;

  return {
    id,
    firebaseId,
    name: typeof raw.name === "string" ? raw.name : null,
    email: typeof raw.email === "string" ? raw.email : null,
    sms: typeof raw.sms === "string" ? raw.sms : null,
    stripeId: typeof raw.stripeId === "string" ? raw.stripeId : null,
    viewedUpdateLog:
      typeof raw.viewedUpdateLog === "boolean"
        ? raw.viewedUpdateLog
        : Boolean(raw.viewedUpdateLog),
    displayName: typeof raw.displayName === "string" ? raw.displayName : null,
    lastLogin: typeof raw.lastLogin === "string" ? raw.lastLogin : null,
    firebaseUser,
  };
};

const isBoolean = (value: unknown): value is boolean =>
  typeof value === "boolean";

const DASHBOARD_OPS_QUERY_POLICY = {
  // Dashboard ops widgets are passive health cards. Denials should stay local to cards.
  suppressAccessDeniedModal: true,
};

const DASHBOARD_OPS_RETRY_POLICY = {
  // Fail fast so each card can surface its own inline fallback quickly.
  maxRetries: 1,
};

export interface AdminV2RbacStaffUsersQueryArgs {
  search?: string;
  limit?: number;
  suppressAccessDeniedModal?: boolean;
}

const coerceDateLike = (value: unknown): string | Date | null => {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return null;
};

const coerceString = (value: unknown): string | null =>
  typeof value === "string" && value.trim().length > 0 ? value : null;

const isOrganisationUsageActivityStatus = (
  value: unknown,
): value is OrganisationUsageActivityStatus =>
  value === "active" ||
  value === "inactive" ||
  value === "cancelled" ||
  value === "unknown";

const toOrganisation = (candidate: unknown): Organisation | null => {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const raw = candidate as Record<string, unknown>;
  const id = typeof raw.id === "string" ? raw.id : null;
  const name = typeof raw.name === "string" ? raw.name : null;

  if (!id || !name) {
    return null;
  }

  const subscriptionStatus =
    typeof raw.subscriptionStatus === "number"
      ? raw.subscriptionStatus
      : OrgSubscriptionStatus.none;

  const users = Array.isArray(raw.users)
    ? raw.users
        .map(toOrganisationUser)
        .filter((user): user is OrganisationUser => user !== null)
    : [];

  const provider =
    raw.provider === "quickbooks" || raw.provider === "xero"
      ? raw.provider
      : null;

  return {
    id,
    tenantId: coerceString(raw.tenantId),
    name,
    subscriptionStatus,
    organisationType:
      typeof raw.organisationType === "number" ||
      typeof raw.organisationType === "string"
        ? raw.organisationType
        : null,
    provider,
    baseCurrency: coerceString(raw.baseCurrency),
    industry: coerceString(raw.industry),
    countryCode: coerceString(raw.countryCode),
    showAccural:
      isBoolean(raw.showAccural) || typeof raw.showAccural === "string"
        ? raw.showAccural
        : null,
    paysTax: isBoolean(raw.paysTax) ? raw.paysTax : null,
    operationsStartDate: coerceDateLike(raw.operationsStartDate),
    lastFullDataRefresh: coerceDateLike(raw.lastFullDataRefresh),
    createdAt: coerceDateLike(raw.createdAt),
    created_at: coerceDateLike(raw.created_at),
    itemsUpdated: coerceDateLike(raw.itemsUpdated),
    usageActivityStatus: isOrganisationUsageActivityStatus(
      raw.usageActivityStatus,
    )
      ? raw.usageActivityStatus
      : undefined,
    usageActiveMinutesLast7Days: coerceNumber(
      raw.usageActiveMinutesLast7Days,
      0,
    ),
    usageLastActiveDate:
      typeof raw.usageLastActiveDate === "string"
        ? raw.usageLastActiveDate
        : raw.usageLastActiveDate === null
          ? null
          : undefined,
    parentOrganisationId:
      typeof raw.parentOrganisationId === "string"
        ? raw.parentOrganisationId
        : null,
    users,
  };
};

/**
 * Navigation PRD §6: once the admin's own live-alert change has succeeded,
 * the badge re-reads its count, bypassing the server's short cache. Only
 * after success: a read started earlier would cache the old count.
 */
async function refreshLiveAlertsCountAfterOwnAction(
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: { dispatch: Dispatch; queryFulfilled: Promise<unknown> },
): Promise<void> {
  try {
    await queryFulfilled;
  } catch {
    return;
  }
  requestFreshLiveAlertsCount();
  dispatch(
    apiSlice.util.invalidateTags([{ type: "AdminV2LiveAlerts", id: "COUNT" }]),
  );
}

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: enhancedBaseQuery,
  // Comprehensive tag types for cache invalidation
  tagTypes: [
    "Organisation",
    "User",
    "EmailTemplate",
    "ContentBlock",
    "Log",
    "CashflowDataQuality",
    "BusinessType",
    "SpoofSession",
    "ProviderToken",
    "SyncData",
    "StripeSubscription",
    "StripePrice",
    "StripeProduct",
    "StripeSubscriptionObjects",
    "AdminUserWithClaims",
    "UsageMetrics",
    "DemoAccess",
    "BulkEmailJob",
    "BugReport",
    "FeatureRequest",
    "AdminMetrics",
    "AdminSubscriptionLocalSnapshot",
    "AdminSubscriptionAuditRun",
    "AdminSubscriptionAction",
    "AdminSubscriptionStripeSummary",
    "DocsShareLink",
    "AdminV2Identity",
    "AdminV2Assistant",
    "AdminV2Approvals",
    "AdminV2AuditLog",
    "AdminV2PersonalCommunicationDrafts",
    "AdminV2Jobs",
    "AdminV2RevenueAccess",
    "AdminV2RevenueMrrScoreboard",
    "AdminV2RevenueTransactions",
    "AdminV2RevenueSubscriptions",
    "AdminV2RevenueDisputes",
    "AdminV2RevenueOffers",
    "AdminV2RevenueOnboardingFlows",
    "AdminV2RevenueCoupons",
    "AdminV2RevenueAffiliates",
    "AdminV2FeatureFlags",
    "AdminV2Entitlements",
    "AdminV2FeatureReadiness",
    "AdminV2RbacStaffUsers",
    "AdminV2RbacRoles",
    "AdminV2RbacPermissions",
    "AdminV2CrmPeople",
    "AdminV2CrmUsers",
    "AdminV2CrmOrganisations",
    "AdminV2CrmTimeline",
    "AdminV2ProfileBilling",
    "AdminV2CrmUserInternalNotes",
    "AdminV2CrmRecordInternalNotes",
    "AdminV2CrmSupportStateMatrix",
    "AdminV2CrmDuplicates",
    "AdminV2CrmIdentityReview",
    "AdminV2CrmActionScopes",
    "AdminV2CrmBusinesses",
    "AdminV2CrmFirms",
    "AdminV2CrmFirmClientRelationships",
    "AdminV2LearningContent",
    "AdminV2LearningPathways",
    "AdminV2LearningAssignments",
    "AdminV2LearningProgress",
    "AdminV2LearningScores",
    "AdminV2LearningScoreRules",
    "AdminV2SalesDeals",
    "AdminV2SalesScores",
    "AdminV2SalesWorkQueues",
    "AdminV2SalesReporting",
    "AdminV2Integrations",
    "AdminV2SageBetaInvites",
    "AdminV2EventTaxonomy",
    "AdminV2Ops",
    "AdminV2GrowthForms",
    "AdminV2FormFieldLibrary",
    "AdminV2GrowthTags",
    "AdminV2GrowthSegments",
    "AdminV2GrowthCampaigns",
    "AdminV2GrowthSequences",
    "AdminV2Automations",
    "AdminV2IssueReports",
    "AdminV2ReleaseReview",
    "AdminV2LiveAlerts",
    "AdminV2PlatformStatus",
    "AdminV2MessagingSenderIdentities",
    "AdminV2MessagingSettings",
    "AdminV2MessagingTemplates",
    "AdminV2MessagingPreferences",
    "AdminV2MessagingEmail",
    "AdminV2MessagingInApp",
    "AdminV2MessagingEmailActivity",
    "AdminV2HelpCollections",
    "AdminV2HelpArticles",
    "AdminV2HelpSearchLogs",
    "AdminV2HelpFeedback",
    "AdminV2SupportTestReset",
    "AdminV2SupportPreviewKeepList",
    "AdminV2SupportConversation",
  ],
  // Admin operators often jump between routes while investigating one issue.
  // Keep list/detail data warm for a few minutes to avoid churn, but still
  // allow reconnect-based refresh when the browser has been offline.
  keepUnusedDataFor: 300,
  refetchOnFocus: false,
  refetchOnReconnect: true,
  refetchOnMountOrArgChange: 300,
  endpoints: (builder) => ({
    // Logs endpoint with proper caching - exactly matches legacy API structure
    getLogs: builder.query<Log[], GetLogsArgs | number | void>({
      query: (args) => ({
        url: "/admin/logs",
        params:
          typeof args === "number" || args === undefined
            ? { pageSize: args }
            : {
                pageSize: args.pageSize,
                organisationId: args.organisationId,
                syncId: args.syncId,
              },
      }),
      providesTags: ["Log"],
      transformResponse: (res: RawLog[]) =>
        res.map((log) => ({
          ...log,
          timestamp: new Date(log.timestamp),
        })),
      // Logs don't need to be cached as long
      keepUnusedDataFor: 120, // 2 minutes
    }),

    // Cross-org worklist of organisations whose synced cashflow fails the
    // reconciliation / attribution checks. Empty until the evidence-writes flag is on.
    getCashflowDataQuality: builder.query<CashflowDataQualityFlag[], void>({
      query: () => ({ url: "/admin/cashflow-data-quality" }),
      providesTags: ["CashflowDataQuality"],
      transformResponse: (res: RawCashflowDataQualityFlag[]) =>
        res.map((row) => ({
          ...row,
          latestCapturedAt: new Date(row.latestCapturedAt),
        })),
      keepUnusedDataFor: 120,
    }),

    getAdminV2PlatformStatus: builder.query<
      AdminV2PlatformStatusResponse,
      void
    >({
      query: () => ({ url: "/admin/v2/ops/platform-status" }),
      providesTags: [{ type: "AdminV2PlatformStatus", id: "SNAPSHOT" }],
      keepUnusedDataFor: 30,
    }),

    // Exact organisation list backing a "Past incidents" org-count phrase's
    // hover tooltip. Fetched lazily on first hover, not with the snapshot
    // above, since most incident rows are never hovered.
    getAdminV2IncidentOrganisations: builder.query<
      AdminV2IncidentOrganisationsResponse,
      { incidentId: string }
    >({
      query: ({ incidentId }) => ({
        url: `/admin/v2/ops/platform-status/incidents/${incidentId}/organisations`,
      }),
      keepUnusedDataFor: 60,
    }),

    // Per-org sync search backing the "Per-org sync health" card's search
    // form: matches by business name, healthy or not, unlike the default
    // failing-orgs worklist above.
    getAdminV2OrgSyncSearch: builder.query<
      AdminV2OrgSyncSearchResponse,
      { q: string }
    >({
      query: ({ q }) => ({
        url: "/admin/v2/ops/org-sync-search",
        params: { q },
      }),
      keepUnusedDataFor: 30,
    }),

    getSpoofSessions: builder.query<
      AdminSpoofSessionsResponse,
      { limit?: number } | void
    >({
      query: (params = {}) => ({
        url: "/admin/spoof-sessions",
        params,
      }),
      providesTags: [{ type: "SpoofSession", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    getQuickbooksTrials: builder.query<AdminQuickbooksTrialsResponse, void>({
      query: () => ({
        url: "/admin/quickbooks-trials",
      }),
      keepUnusedDataFor: 60,
    }),

    getBulkEmailJobs: builder.query<
      AdminBulkEmailJobsResponse,
      { limit?: number; status?: string } | void
    >({
      query: (params = {}) => ({
        url: "/admin/bulk-email-jobs",
        params,
      }),
      providesTags: [{ type: "BulkEmailJob", id: "LIST" }],
      keepUnusedDataFor: 30,
    }),

    // New Optimized Admin Endpoints (Backend Changes)

    /**
     * @deprecated Admin v2 Sprint 2 no longer depends on `/admin/organisations`
     * for the people/users relationship workflow. Use the `/admin/v2/crm/organisations`
     * endpoints for new admin v2 work and keep this query only for legacy admin
     * surfaces until cleanup.
     */
    getOrganisations: builder.query<
      AdminOrganisationSummary[],
      {
        search?: string;
        organisationType?: string;
        sortBy?: string;
      }
    >({
      query: (params) => ({
        url: "/admin/organisations",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "Organisation" as const,
                id,
              })),
              { type: "Organisation", id: "LIST" },
            ]
          : [{ type: "Organisation", id: "LIST" }],
      transformResponse: (res: unknown[] = []): AdminOrganisationSummary[] =>
        res
          .map(toOrganisation)
          .filter(
            (organisation): organisation is AdminOrganisationSummary =>
              organisation !== null,
          ),
      // Cache organisations longer since they change less frequently
      keepUnusedDataFor: 600, // 10 minutes
    }),

    removeOrganisationUser: builder.mutation<
      RemoveOrganisationUserResponse,
      RemoveOrganisationUserRequest
    >({
      query: ({ organisationId, userId }) => ({
        url: `/admin/organisations/${organisationId}/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { organisationId }) => [
        { type: "Organisation", id: organisationId },
        { type: "Organisation", id: "LIST" },
        { type: "AdminV2CrmUsers", id: "LIST" },
        // CRITICAL RORY-041: refresh the header/rail and list membership counts.
        { type: "AdminV2CrmUsers", id: `organisation:${organisationId}` },
        // The complete access list determines whether the last admin is protected.
        { type: "AdminV2CrmOrganisations", id: organisationId },
        { type: "AdminV2CrmOrganisations", id: "LIST" },
      ],
    }),

    // Detailed user data with organisation context - optimized for user management
    getUsers: builder.query<
      Array<{
        id: string;
        firebaseId: string;
        email: string;
        displayName?: string;
        activity_status:
          "active" | "inactive" | "never_logged_in" | "cancelled" | "unknown";
        lastLoginAt: string | null;
        loginCount: number;
        signupDate: string;
        usageActiveMinutesLast7Days: number;
        usageLastActiveDate: string | null;
        organisation_name: string;
        organisation_type: string;
        subscription_status: number;
        authProviders: AuthProvider[];
        providerConnections?: unknown[];
      }>,
      {
        search?: string;
        activityStatus?: string;
        sortBy?: string;
        sortOrder?: "asc" | "desc";
      }
    >({
      query: (params) => ({
        url: "/admin/users",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "User" as const,
                id,
              })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
      transformResponse: (res: AllUser[]) => res.map(toAdminUserSummary),
      // Users data cached for shorter time as activity changes frequently
      keepUnusedDataFor: 180, // 3 minutes
    }),

    getProviderConnectionOrganisations: builder.query<
      AdminOrganisationSummary[],
      string
    >({
      query: (connectionId) => ({
        url: `/admin/provider-connection/${connectionId}/organisations`,
      }),
      providesTags: ["Organisation"],
      keepUnusedDataFor: 300,
    }),

    // Get dashboard statistics for admin
    getDashboardStatistics: builder.query<AdminStatisticsResponse, void>({
      query: () => ({
        url: "/admin/statistics",
      }),
      providesTags: ["SyncData"],
      transformResponse: (res: unknown): AdminStatisticsResponse => {
        const raw = res as Record<string, unknown>;
        const users =
          raw.users && typeof raw.users === "object"
            ? (raw.users as Record<string, unknown>)
            : {};
        const organizations =
          raw.organizations && typeof raw.organizations === "object"
            ? (raw.organizations as Record<string, unknown>)
            : {};
        const subscriptions =
          raw.subscriptions && typeof raw.subscriptions === "object"
            ? (raw.subscriptions as Record<string, unknown>)
            : {};
        const stripe = raw.stripe as AdminStatisticsResponse["stripe"];

        return {
          stripe,
          users: {
            total_users: coerceNumber(users.total_users),
            active_users: coerceNumber(users.active_users),
            never_logged_in: coerceNumber(users.never_logged_in),
          },
          organizations: {
            total_orgs: coerceNumber(organizations.total_orgs),
            advisor_accounts: coerceNumber(organizations.advisor_accounts),
            business_accounts: coerceNumber(organizations.business_accounts),
          },
          subscriptions: {
            monthly_subs: coerceNumber(subscriptions.monthly_subs),
            lifetime_spots: coerceNumber(subscriptions.lifetime_spots),
            cancelled: coerceNumber(subscriptions.cancelled),
          },
        };
      },
      // Statistics change less frequently
      keepUnusedDataFor: 300, // 5 minutes
    }),

    getAdminMetrics: builder.query<AdminMetricsResponse, void>({
      query: () => ({
        url: "/admin/admin-metrics",
      }),
      providesTags: ["AdminMetrics"],
      keepUnusedDataFor: 60,
    }),

    getAdminV2Me: builder.query<AdminV2MeResponse, void>({
      query: () => ({
        url: "/admin/v2/me",
      }),
      providesTags: [{ type: "AdminV2Identity", id: "ME" }],
      // Permission-aware routes use this as local authorization context,
      // not just display data, so keep it fresh enough for gated actions.
      keepUnusedDataFor: 60,
    }),

    uploadAdminV2ProfilePhoto: builder.mutation<
      AdminV2ProfilePhotoUploadResponse,
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append("photo", file);

        return {
          url: "/admin/v2/me/profile-photo",
          method: "PUT",
          data: formData,
        };
      },
      invalidatesTags: [
        { type: "AdminV2Identity", id: "ME" },
        { type: "AdminV2RbacStaffUsers", id: "LIST" },
      ],
    }),

    getAdminV2LiveAlerts: builder.query<
      AdminV2LiveAlertsListResponse,
      {
        q?: string;
        source?: AdminV2LiveAlertSource;
        excludeSources?: string;
        sourceSystem?: "forms";
        eventType?: string;
        priority?: AdminV2LiveAlertPriority;
        status?: AdminV2LiveAlertStatus;
        adminLeadStaffUserId?: string;
        unassignedOnly?: boolean;
        organisationId?: string;
        personId?: string;
        userId?: string;
        dateFrom?: string;
        dateTo?: string;
        limit?: number;
        offset?: number;
        sort?: AdminV2LiveAlertSort;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/live-alerts",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2LiveAlerts", id: "LIST" }],
    }),

    getAdminV2LiveAlert: builder.query<
      AdminV2LiveAlertDetailResponse,
      {
        source: AdminV2LiveAlertSource;
        sourceRecordId: string;
        /** Client-only read lifetime; default RTK key includes it. */
        readOwner?: string;
      }
    >({
      query: ({ source, sourceRecordId }) => ({
        url: `/admin/v2/live-alerts/${source}/${encodeURIComponent(
          sourceRecordId,
        )}`,
      }),
      providesTags: (_result, _error, { source, sourceRecordId }) => [
        {
          type: "AdminV2LiveAlerts",
          id: `${source}:${sourceRecordId}`,
        },
      ],
    }),

    getAdminV2LiveAlertStaffUsers: builder.query<
      AdminV2LiveAlertStaffUsersResponse,
      { search?: string; limit?: number; assignableOnly?: boolean } | void
    >({
      query: (params) => ({
        url: "/admin/v2/live-alerts/staff-users",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2LiveAlerts", id: "STAFF_USERS" }],
      keepUnusedDataFor: 60,
    }),

    createAdminV2ManualLiveAlert: builder.mutation<
      AdminV2LiveAlertMutationResponse,
      AdminV2CreateManualLiveAlertRequest
    >({
      query: (body) => ({
        url: "/admin/v2/live-alerts/manual",
        method: "POST",
        data: body,
      }),
      onQueryStarted: refreshLiveAlertsCountAfterOwnAction,
      invalidatesTags: [
        { type: "AdminV2LiveAlerts", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    addAdminV2LiveAlertNote: builder.mutation<
      AdminV2LiveAlertMutationResponse,
      {
        source: AdminV2LiveAlertSource;
        sourceRecordId: string;
        body: AdminV2AddLiveAlertNoteRequest;
      }
    >({
      query: ({ source, sourceRecordId, body }) => ({
        url: `/admin/v2/live-alerts/${source}/${encodeURIComponent(
          sourceRecordId,
        )}/notes`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (result, _error, { source, sourceRecordId }) => [
        { type: "AdminV2LiveAlerts", id: "LIST" },
        {
          type: "AdminV2LiveAlerts",
          id: `${source}:${sourceRecordId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
        ...(result ? getLiveAlertRelatedRecordTags(result.alert) : []),
      ],
    }),

    recordAdminV2LiveAlertQuickAction: builder.mutation<
      AdminV2RecordLiveAlertQuickActionResponse,
      {
        source: AdminV2LiveAlertSource;
        sourceRecordId: string;
        body: AdminV2RecordLiveAlertQuickActionRequest;
      }
    >({
      query: ({ source, sourceRecordId, body }) => ({
        url: `/admin/v2/live-alerts/${source}/${encodeURIComponent(
          sourceRecordId,
        )}/actions`,
        method: "POST",
        data: body,
      }),
      onQueryStarted: refreshLiveAlertsCountAfterOwnAction,
      invalidatesTags: (_result, _error, { source, sourceRecordId }) => [
        { type: "AdminV2LiveAlerts", id: "LIST" },
        {
          type: "AdminV2LiveAlerts",
          id: `${source}:${sourceRecordId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2LiveAlert: builder.mutation<
      AdminV2LiveAlertMutationResponse,
      {
        source: AdminV2LiveAlertSource;
        sourceRecordId: string;
        body: AdminV2UpdateLiveAlertRequest;
      }
    >({
      query: ({ source, sourceRecordId, body }) => ({
        url: `/admin/v2/live-alerts/${source}/${encodeURIComponent(
          sourceRecordId,
        )}`,
        method: "PATCH",
        data: body,
      }),
      onQueryStarted: refreshLiveAlertsCountAfterOwnAction,
      invalidatesTags: (_result, _error, { source, sourceRecordId }) => [
        { type: "AdminV2LiveAlerts", id: "LIST" },
        {
          type: "AdminV2LiveAlerts",
          id: `${source}:${sourceRecordId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    archiveAdminV2LiveAlert: builder.mutation<
      AdminV2LiveAlertMutationResponse,
      {
        source: AdminV2LiveAlertSource;
        sourceRecordId: string;
      }
    >({
      query: ({ source, sourceRecordId }) => ({
        url: `/admin/v2/live-alerts/${source}/${encodeURIComponent(
          sourceRecordId,
        )}/archive`,
        method: "POST",
        data: {},
      }),
      onQueryStarted: refreshLiveAlertsCountAfterOwnAction,
      invalidatesTags: (_result, _error, { source, sourceRecordId }) => [
        { type: "AdminV2LiveAlerts", id: "LIST" },
        {
          type: "AdminV2LiveAlerts",
          id: `${source}:${sourceRecordId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    ...buildAdminV2AssistantEndpoints(builder),
    ...buildAdminV2LiveAlertCountEndpoints(builder),
    ...buildAdminV2RevenueEndpoints(builder),

    ...buildAdminV2LearningEndpoints(builder),

    getAdminV2SalesDeals: builder.query<
      AdminV2SalesDealsResponse,
      {
        organisationId?: string;
        ownerStaffUserId?: string;
        segment?: string;
        stage?: AdminV2SalesStage;
        scoreBand?: AdminV2SalesScoreBand;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/sales/deals",
        params: optionalQueryParams(params),
      }),
      providesTags: (result) =>
        providesList(result?.deals ?? [], "AdminV2SalesDeals"),
      keepUnusedDataFor: 20,
    }),

    createAdminV2SalesDeal: builder.mutation<
      AdminV2SalesDealMutationResponse,
      {
        organisationId: string;
        personId?: string;
        businessId?: string;
        title: string;
        stage?: AdminV2SalesStage;
        ownerStaffUserId?: string;
        segment?: string;
        amountCents?: number;
        currency?: string;
        closeProbabilityPercent?: number;
        expectedCloseDate?: string;
        nextActionAt?: string;
        metadata?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/sales/deals",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2SalesDeals", id: "LIST" },
        { type: "AdminV2SalesScores", id: "LIST" },
        { type: "AdminV2SalesWorkQueues", id: "LIST" },
        { type: "AdminV2SalesReporting", id: "SUMMARY" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2SalesDealStage: builder.mutation<
      AdminV2SalesDealMutationResponse,
      {
        dealId: string;
        body: {
          stage: AdminV2SalesStage;
          note?: string;
        };
      }
    >({
      query: ({ dealId, body }) => ({
        url: `/admin/v2/sales/deals/${dealId}/stage`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { dealId }) => [
        { type: "AdminV2SalesDeals", id: "LIST" },
        { type: "AdminV2SalesDeals", id: dealId },
        { type: "AdminV2SalesScores", id: "LIST" },
        { type: "AdminV2SalesWorkQueues", id: "LIST" },
        { type: "AdminV2SalesReporting", id: "SUMMARY" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    claimAdminV2SalesDeal: builder.mutation<
      AdminV2SalesDealEventMutationResponse,
      {
        dealId: string;
        body?: {
          assigneeStaffUserId?: string;
          expectedOwnerStaffUserId?: string | null;
          note?: string;
        };
      }
    >({
      query: ({ dealId, body }) => ({
        url: `/admin/v2/sales/deals/${dealId}/claim`,
        method: "POST",
        data: optionalMutationBody(body),
      }),
      invalidatesTags: (_result, _error, { dealId }) => [
        { type: "AdminV2SalesDeals", id: "LIST" },
        { type: "AdminV2SalesDeals", id: dealId },
        { type: "AdminV2SalesWorkQueues", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    addAdminV2SalesDealNote: builder.mutation<
      AdminV2SalesDealEventMutationResponse,
      {
        dealId: string;
        body: {
          note: string;
        };
      }
    >({
      query: ({ dealId, body }) => ({
        url: `/admin/v2/sales/deals/${dealId}/notes`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { dealId }) => [
        { type: "AdminV2SalesDeals", id: "LIST" },
        { type: "AdminV2SalesDeals", id: dealId },
        { type: "AdminV2SalesWorkQueues", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2SalesDealEvents: builder.query<
      AdminV2SalesDealEventsResponse,
      {
        dealId: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ dealId, ...params }) => ({
        url: `/admin/v2/sales/deals/${dealId}/events`,
        params,
      }),
      providesTags: (_result, _error, { dealId }) => [
        { type: "AdminV2SalesDeals", id: dealId },
      ],
      keepUnusedDataFor: 20,
    }),

    getAdminV2SalesLeadScores: builder.query<
      AdminV2SalesLeadScoresResponse,
      {
        organisationId?: string;
        personId?: string;
        ownerStaffUserId?: string;
        segment?: string;
        scoreBand?: AdminV2SalesScoreBand;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/sales/scores/leads",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2SalesScores", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    getAdminV2SalesMemberScores: builder.query<
      AdminV2SalesMemberScoresResponse,
      {
        organisationId?: string;
        businessId?: string;
        ownerStaffUserId?: string;
        segment?: string;
        scoreBand?: AdminV2SalesScoreBand;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/sales/scores/members",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2SalesScores", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    recomputeAdminV2SalesScores: builder.mutation<
      AdminV2SalesRecomputeScoresResponse,
      {
        organisationId: string;
        personId?: string;
        businessId?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/sales/scores/recompute",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2SalesScores", id: "LIST" },
        { type: "AdminV2SalesWorkQueues", id: "LIST" },
        { type: "AdminV2SalesReporting", id: "SUMMARY" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2SalesWorkQueues: builder.query<
      AdminV2SalesWorkQueueResponse,
      {
        role?: AdminV2SalesQueueRole;
        organisationId?: string;
        ownerStaffUserId?: string;
        segment?: string;
        scoreBand?: AdminV2SalesScoreBand;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/sales/work-queues",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2SalesWorkQueues", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    getAdminV2SalesReportingSummary: builder.query<
      AdminV2SalesReportingSummaryResponse,
      {
        organisationId: string;
      }
    >({
      query: (params) => ({
        url: "/admin/v2/sales/reporting/summary",
        params,
      }),
      providesTags: [{ type: "AdminV2SalesReporting", id: "SUMMARY" }],
      keepUnusedDataFor: 10,
    }),

    getAdminV2Integrations: builder.query<
      AdminV2IntegrationsResponse,
      { organisationId?: string } | void
    >({
      query: (params) => ({
        url: "/admin/v2/integrations",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2Integrations", id: "LIST" }],
      keepUnusedDataFor: 15,
    }),

    upsertAdminV2IntegrationConnection: builder.mutation<
      AdminV2IntegrationConnectionResponse,
      {
        organisationId?: string | null;
        integrationKey: string;
        status?: AdminV2IntegrationConnectionStatus;
        connectionLabel?: string | null;
        connectionConfig?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/integrations/connections",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2Integrations", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    patchAdminV2IntegrationConnection: builder.mutation<
      AdminV2IntegrationConnectionResponse,
      {
        connectionId: string;
        body: {
          status?: AdminV2IntegrationConnectionStatus;
          connectionLabel?: string | null;
          connectionConfig?: AdminV2JsonValue;
        };
      }
    >({
      query: ({ connectionId, body }) => ({
        url: `/admin/v2/integrations/connections/${connectionId}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2Integrations", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2IntegrationConnectionHealth: builder.query<
      AdminV2IntegrationConnectionHealthResponse,
      {
        connectionId: string;
      }
    >({
      query: ({ connectionId }) => ({
        url: `/admin/v2/integrations/connections/${connectionId}/health`,
      }),
      providesTags: [{ type: "AdminV2Integrations", id: "LIST" }],
      keepUnusedDataFor: 15,
    }),

    getAdminV2SageBetaInvites: builder.query<
      AdminV2SageBetaInvitesResponse,
      {
        organisationId?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/sage/beta-invites",
        params: optionalQueryParams(params),
      }),
      providesTags: (_result, _error, args) => [
        { type: "AdminV2SageBetaInvites", id: "LIST" },
        ...(args?.organisationId
          ? [
              {
                type: "AdminV2SageBetaInvites" as const,
                id: `organisation:${args.organisationId}`,
              },
            ]
          : []),
      ],
      keepUnusedDataFor: 10,
    }),

    createAdminV2SageBetaInvite: builder.mutation<
      AdminV2SageBetaInviteMutationResponse,
      {
        intendedEmail?: string;
        organisationId?: string;
        sendEmail?: boolean;
        sendActionId?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/sage/beta-invites",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "AdminV2SageBetaInvites", id: "LIST" },
        ...(args.organisationId
          ? [
              {
                type: "AdminV2SageBetaInvites" as const,
                id: `organisation:${args.organisationId}`,
              },
            ]
          : []),
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    revokeAdminV2SageBetaInvite: builder.mutation<
      AdminV2SageBetaInviteRevokeResponse,
      {
        inviteId: string;
        organisationId?: string | null;
      }
    >({
      query: ({ inviteId }) => ({
        url: `/admin/v2/sage/beta-invites/${inviteId}/revoke`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "AdminV2SageBetaInvites", id: "LIST" },
        ...(args.organisationId
          ? [
              {
                type: "AdminV2SageBetaInvites" as const,
                id: `organisation:${args.organisationId}`,
              },
            ]
          : []),
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    reissueAdminV2SageBetaInvite: builder.mutation<
      AdminV2SageBetaInviteMutationResponse,
      {
        inviteId: string;
        organisationId?: string | null;
        sendEmail?: boolean;
        sendActionId?: string;
      }
    >({
      query: ({ inviteId, sendEmail, sendActionId }) => ({
        url: `/admin/v2/sage/beta-invites/${inviteId}/reissue`,
        method: "POST",
        data: {
          ...(sendEmail ? { sendEmail } : {}),
          ...(sendActionId ? { sendActionId } : {}),
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        { type: "AdminV2SageBetaInvites", id: "LIST" },
        ...(args.organisationId
          ? [
              {
                type: "AdminV2SageBetaInvites" as const,
                id: `organisation:${args.organisationId}`,
              },
            ]
          : []),
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2PosthogDashboards: builder.query<
      AdminV2PosthogDashboardsResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/insights/posthog/dashboards",
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2EventTaxonomy: builder.query<AdminV2EventTaxonomyResponse, void>({
      query: () => ({
        url: "/admin/v2/insights/event-taxonomy",
      }),
      providesTags: [{ type: "AdminV2EventTaxonomy", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    getAdminV2OnboardingFunnel: builder.query<
      AdminV2OnboardingFunnelResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/onboarding-funnel",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2ActivationReport: builder.query<
      AdminV2ActivationReportResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/activation",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2AnalyticsPipelineHealth: builder.query<
      AdminV2AnalyticsPipelineHealthResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/analytics-health",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2InteractionElements: builder.query<
      AdminV2InteractionElementsResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
        routeId?: string;
        elementId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/interactions/elements",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2InteractionHeatmapGrid: builder.query<
      AdminV2InteractionHeatmapGridResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
        routeId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/interactions/heatmap-grid",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2InteractionUnknownElements: builder.query<
      AdminV2InteractionUnknownElementsResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
        routeId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/interactions/unknown-elements",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2InteractionPipelineHealth: builder.query<
      AdminV2InteractionPipelineHealthResponse,
      {
        startDate?: string;
        endDate?: string;
        sourceEnvironment?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/interactions/pipeline-health",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2AiInsightsQuality: builder.query<
      AdminV2AiInsightsQualityResponse,
      {
        startDate?: string;
        endDate?: string;
        organisationId?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/ai-insights-quality",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    getAdminV2OpenAiUsage: builder.query<
      AdminV2OpenAiUsageResponse,
      {
        startDate?: string;
        endDate?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/insights/openai-usage",
        params,
      }),
      keepUnusedDataFor: 60,
    }),

    patchAdminV2EventTaxonomy: builder.mutation<
      AdminV2PatchEventTaxonomyResponse,
      {
        eventName: string;
        body: {
          status?: AdminV2ProductEventTaxonomyStatus;
          annotation?: string | null;
          deprecatedReason?: string | null;
          replacementEventName?: string | null;
        };
      }
    >({
      query: ({ eventName, body }) => ({
        url: `/admin/v2/insights/event-taxonomy/${encodeURIComponent(
          eventName,
        )}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2EventTaxonomy", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2OpsWhatNeedsAttention: builder.query<
      AdminV2OpsWhatNeedsAttentionResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/ops/what-needs-attention",
        ...DASHBOARD_OPS_QUERY_POLICY,
      }),
      extraOptions: DASHBOARD_OPS_RETRY_POLICY,
      providesTags: [{ type: "AdminV2Ops", id: "ATTENTION" }],
      keepUnusedDataFor: 15,
    }),

    getAdminV2OpsIngestFailures: builder.query<
      AdminV2OpsIngestFailuresResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/ops/ingest-failures",
        ...DASHBOARD_OPS_QUERY_POLICY,
      }),
      extraOptions: DASHBOARD_OPS_RETRY_POLICY,
      providesTags: [{ type: "AdminV2Ops", id: "INGEST" }],
      keepUnusedDataFor: 15,
    }),

    getAdminV2OpsDeliverabilityHealth: builder.query<
      AdminV2OpsDeliverabilityHealthResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/ops/deliverability-health",
        ...DASHBOARD_OPS_QUERY_POLICY,
      }),
      extraOptions: DASHBOARD_OPS_RETRY_POLICY,
      providesTags: [{ type: "AdminV2Ops", id: "DELIVERABILITY" }],
      keepUnusedDataFor: 15,
    }),

    getAdminV2OpsSupportBacklog: builder.query<
      AdminV2OpsSupportBacklogResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/ops/support-backlog",
        ...DASHBOARD_OPS_QUERY_POLICY,
      }),
      extraOptions: DASHBOARD_OPS_RETRY_POLICY,
      providesTags: [{ type: "AdminV2Ops", id: "SUPPORT_BACKLOG" }],
      keepUnusedDataFor: 15,
    }),

    getAdminV2FeatureFlags: builder.query<
      AdminV2FeatureFlagsResponse,
      {
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/settings/feature-flags",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2FeatureFlags", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    upsertAdminV2FeatureFlag: builder.mutation<
      AdminV2FeatureFlagUpsertResponse,
      {
        organisationId?: string;
        flagKey: string;
        isEnabled: boolean;
        rolloutPercentage?: number;
        description?: string;
        metadata?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/settings/feature-flags",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2FeatureFlags", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
        { type: "AdminV2RevenueAccess", id: "CONTEXT" },
      ],
    }),

    getAdminV2Entitlements: builder.query<
      AdminV2EntitlementsResponse,
      {
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/settings/entitlements",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2Entitlements", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2FeatureAccess: builder.query<
      AdminV2FeatureAccessSummaryResponse,
      {
        entitlementKey: string;
        environment?: AdminV2FeatureAccessEnvironment;
      }
    >({
      query: ({ entitlementKey, environment }) => ({
        url: "/admin/v2/settings/feature-access",
        params: {
          entitlementKey,
          ...(environment ? { environment } : {}),
        },
        // A cross-site target this deployment may not manage answers 403
        // (`environment_not_allowed`) by design; the console renders that as
        // the column's "managed elsewhere" hint, so keep the global
        // access-denied modal out of it.
        suppressAccessDeniedModal: true,
      }),
      // Tagged per environment so an entitlement upsert (apply-to-all or
      // per-org) refetches the matching site's overview without also refetching
      // the other site's.
      providesTags: (_result, _error, { environment }) => [
        {
          type: "AdminV2Entitlements",
          id: `LIST-${environment ?? "production"}`,
        },
      ],
      keepUnusedDataFor: 10,
    }),

    getAdminV2FeatureAccessOrganisations: builder.query<
      AdminV2FeatureAccessOrganisationsResponse,
      { environment?: AdminV2FeatureAccessEnvironment }
    >({
      query: ({ environment }) => ({
        url: "/admin/v2/settings/feature-access/organisations",
        params: environment ? { environment } : {},
        // See getAdminV2FeatureAccess: cross-site refusals render as hints.
        suppressAccessDeniedModal: true,
      }),
      providesTags: (_result, _error, { environment }) => [
        {
          type: "Organisation",
          id: `FEATURE-ACCESS-${environment ?? "production"}`,
        },
      ],
      // Organisations change rarely; cache like the legacy organisations list.
      keepUnusedDataFor: 600,
    }),

    getAdminV2FeatureReadiness: builder.query<
      AdminV2FeatureReadinessResponse,
      { entitlementKeys: string[] }
    >({
      query: ({ entitlementKeys }) => ({
        url: "/admin/v2/settings/feature-readiness",
        params: { entitlementKeys: entitlementKeys.join(",") },
      }),
      providesTags: [{ type: "AdminV2FeatureReadiness", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    setAdminV2FeatureReadiness: builder.mutation<
      { entitlementKey: string; ready: boolean },
      { entitlementKey: string; ready: boolean }
    >({
      query: (body) => ({
        url: "/admin/v2/settings/feature-readiness",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2FeatureReadiness", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    upsertAdminV2Entitlement: builder.mutation<
      AdminV2EntitlementUpsertResponse,
      {
        organisationId?: string;
        entitlementKey: string;
        isEnabled: boolean;
        source?: "admin" | "system" | "stripe";
        value?: AdminV2JsonValue | null;
        startsAt?: string;
        expiresAt?: string;
        environment?: AdminV2FeatureAccessEnvironment;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/settings/entitlements",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { environment }) => [
        { type: "AdminV2Entitlements", id: "LIST" },
        {
          type: "AdminV2Entitlements",
          id: `LIST-${environment ?? "production"}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
        { type: "AdminV2RevenueAccess", id: "CONTEXT" },
      ],
    }),

    deleteAdminV2Entitlement: builder.mutation<
      { deleted: boolean },
      {
        entitlementKey: string;
        organisationId?: string;
        environment?: AdminV2FeatureAccessEnvironment;
      }
    >({
      query: ({ entitlementKey, organisationId, environment }) => ({
        url: "/admin/v2/settings/entitlements",
        method: "DELETE",
        params: {
          entitlementKey,
          ...(organisationId ? { organisationId } : {}),
          ...(environment ? { environment } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { environment }) => [
        { type: "AdminV2Entitlements", id: "LIST" },
        {
          type: "AdminV2Entitlements",
          id: `LIST-${environment ?? "production"}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
        { type: "AdminV2RevenueAccess", id: "CONTEXT" },
      ],
    }),

    getAdminV2RbacStaffUsers: builder.query<
      AdminV2RbacStaffUsersResponse,
      AdminV2RbacStaffUsersQueryArgs
    >({
      query: (params) => {
        const { suppressAccessDeniedModal = false, ...queryParams } = params;

        return {
          url: "/admin/v2/rbac/staff-users",
          params: optionalQueryParams(queryParams),
          suppressAccessDeniedModal,
        };
      },
      providesTags: [{ type: "AdminV2RbacStaffUsers", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2RbacRoles: builder.query<AdminV2RbacRolesResponse, void>({
      query: () => ({
        url: "/admin/v2/rbac/roles",
      }),
      providesTags: [{ type: "AdminV2RbacRoles", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    getAdminV2RbacPermissions: builder.query<
      AdminV2RbacPermissionsResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/rbac/permissions",
      }),
      providesTags: [{ type: "AdminV2RbacPermissions", id: "LIST" }],
      keepUnusedDataFor: 60,
    }),

    assignAdminV2StaffRole: builder.mutation<
      { staffUser: AdminV2RbacStaffUsersResponse["staffUsers"][number] },
      { staffUserId: string; roleKey: string; assign: boolean }
    >({
      query: ({ staffUserId, roleKey, assign }) => ({
        url: `/admin/v2/rbac/staff-users/${staffUserId}/roles`,
        method: "POST",
        data: {
          roleKey,
          assign,
        },
      }),
      invalidatesTags: [
        { type: "AdminV2RbacStaffUsers", id: "LIST" },
        { type: "AdminV2Identity", id: "ME" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    setAdminV2StaffPermissionOverride: builder.mutation<
      { staffUser: AdminV2RbacStaffUsersResponse["staffUsers"][number] },
      {
        staffUserId: string;
        permissionKey: string;
        isGranted: boolean;
        reason?: string;
      }
    >({
      query: ({ staffUserId, permissionKey, isGranted, reason }) => ({
        url: `/admin/v2/rbac/staff-users/${staffUserId}/permission-overrides`,
        method: "POST",
        data: {
          permissionKey,
          isGranted,
          ...(reason ? { reason } : {}),
        },
      }),
      invalidatesTags: [
        { type: "AdminV2RbacStaffUsers", id: "LIST" },
        { type: "AdminV2Identity", id: "ME" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    searchAdminV2: builder.query<
      AdminV2SearchResponse,
      { q: string; types?: string[]; limit?: number }
    >({
      query: ({ q, types, limit }) => ({
        url: "/admin/v2/search",
        params: {
          q,
          ...(types && types.length > 0 ? { types: types.join(",") } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
      keepUnusedDataFor: 10,
    }),

    getAdminV2Approvals: builder.query<
      AdminV2ApprovalsResponse,
      {
        status?: AdminV2ApprovalStatus;
        requestType?: string;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/approvals",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2Approvals", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    createAdminV2Approval: builder.mutation<
      { approval: AdminV2ApprovalsResponse["approvals"][number] },
      {
        organisationId?: string;
        requestType: string;
        reason: string;
        targetType?: string;
        targetId?: string;
        requiredPermissionKey?: string;
        payload?: AdminV2JsonValue;
        expiresAt?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/approvals",
        method: "POST",
        data: body,
        suppressAccessDeniedModal: true,
      }),
      invalidatesTags: [
        { type: "AdminV2Approvals", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    approveAdminV2Approval: builder.mutation<
      { approval: AdminV2ApprovalsResponse["approvals"][number] },
      {
        approvalRequestId: string;
        organisationId?: string | null;
        comment?: string;
      }
    >({
      query: ({ approvalRequestId, organisationId, comment }) => ({
        url: `/admin/v2/approvals/${approvalRequestId}/approve`,
        method: "POST",
        data: {
          ...(organisationId ? { organisationId } : {}),
          ...(comment ? { comment } : {}),
        },
      }),
      invalidatesTags: [
        { type: "AdminV2Approvals", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    rejectAdminV2Approval: builder.mutation<
      { approval: AdminV2ApprovalsResponse["approvals"][number] },
      {
        approvalRequestId: string;
        organisationId?: string | null;
        comment?: string;
      }
    >({
      query: ({ approvalRequestId, organisationId, comment }) => ({
        url: `/admin/v2/approvals/${approvalRequestId}/reject`,
        method: "POST",
        data: {
          ...(organisationId ? { organisationId } : {}),
          ...(comment ? { comment } : {}),
        },
      }),
      invalidatesTags: [
        { type: "AdminV2Approvals", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    cancelAdminV2Approval: builder.mutation<
      { approval: AdminV2ApprovalsResponse["approvals"][number] },
      {
        approvalRequestId: string;
        organisationId?: string | null;
        comment?: string;
      }
    >({
      query: ({ approvalRequestId, organisationId, comment }) => ({
        url: `/admin/v2/approvals/${approvalRequestId}/cancel`,
        method: "POST",
        data: {
          ...(organisationId ? { organisationId } : {}),
          ...(comment ? { comment } : {}),
        },
      }),
      invalidatesTags: [
        { type: "AdminV2Approvals", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2AuditLog: builder.query<
      AdminV2SafeAuditLogResponse,
      {
        organisationId?: string;
        actor?: string;
        target?: string;
        action?: string;
        fromDate?: string;
        toDate?: string;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/audit-log",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2AuditLog", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2Jobs: builder.query<
      AdminV2JobsResponse,
      {
        status?: "queued" | "running" | "succeeded" | "failed" | "cancelled";
        jobType?: string;
        organisationId?: string;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/jobs",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2Jobs", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2Job: builder.query<AdminV2JobResponse, { jobId: string }>({
      query: ({ jobId }) => ({
        url: `/admin/v2/jobs/${jobId}`,
      }),
      providesTags: (_result, _error, { jobId }) => [
        { type: "AdminV2Jobs", id: jobId },
      ],
      keepUnusedDataFor: 10,
    }),

    cancelAdminV2Job: builder.mutation<
      AdminV2JobCancelResponse,
      { jobId: string }
    >({
      query: ({ jobId }) => ({
        url: `/admin/v2/jobs/${jobId}/cancel`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "AdminV2Jobs", id: "LIST" },
        { type: "AdminV2Jobs", id: jobId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmPeople: builder.query<
      AdminV2CrmPeopleListResponse,
      {
        activeOnly?: boolean;
        /** Mutually exclusive with activeOnly; excludes deleted/merged records. */
        archivedOnly?: boolean;
        includeContactDisplay?: boolean;
        includeTiles?: boolean;
        /** Read-only drilldown for a health-check filter's coverage notice. */
        healthCheckHistory?: "unknown";
        /** Viewer IANA zone for calendar recent-contact totals. */
        timeZone?: string;
        /** TAG-03: tag clauses also see the linked user's tags. */
        tagView?: "linked_pair";
        filters?: string;
        matchLogic?: "all" | "any";
        scopeFilters?: string;
        scopeFilterMatchLogic?: "all" | "any";
        q?: string;
        lifecycleStage?: string;
        pipelineStatus?: string;
        organisationId?: string;
        limit?: number;
        offset?: number;
        sortBy?: "full_name" | "last_activity_at" | "updated_at" | "created_at";
        sortDirection?: "asc" | "desc";
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/people",
        params: optionalQueryParams(params),
        // A refused linked-state filter (M39) is explained on the page.
        ...(hasCrmLinkedStateFilter(params?.filters) ||
        hasCrmLinkedStateFilter(params?.scopeFilters)
          ? { suppressAccessDeniedModal: true }
          : {}),
      }),
      providesTags: [{ type: "AdminV2CrmPeople", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmPeopleFilterSuggestions: builder.query<
      AdminV2CrmFilterSuggestionsResponse,
      {
        activeOnly?: boolean;
        selectedOwnerIds?: string[];
        selectedTagIds?: string[];
        /** Sent in the body with the draft (TAG-03 linked-pair tags). */
        tagView?: "linked_pair";
        search?: string;
        filters?: Array<{
          category: string;
          field: string;
          condition: string;
          value: string | string[];
        }>;
        activeClause: {
          category: string;
          field: string;
          condition: string;
          query: string;
        };
        limit?: number;
        organisationId?: string;
      }
    >({
      query: ({ organisationId, ...body }) => ({
        url: "/admin/v2/crm/people/filter-suggestions",
        method: "POST",
        data: body,
        // A refused linked-state clause (M39) is explained on the page.
        ...(hasCrmLinkedStateClause(body)
          ? { suppressAccessDeniedModal: true }
          : {}),
        params: optionalQueryParams(
          organisationId ? { organisationId } : undefined,
        ),
      }),
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmDuplicateCandidates: builder.query<
      AdminV2CrmDuplicateCandidateListResponse,
      {
        q?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      // Keep the duplicate queue separate from people list caching because
      // preview can create review follow-up without changing the list rows.
      query: (params) => ({
        url: "/admin/v2/crm/duplicates",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmDuplicates", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    getAdminV2CrmDuplicateIdentitiesReport: builder.query<
      {
        items: Array<{
          primaryPerson: { id: string; fullName: string };
          duplicatePerson: { id: string; fullName: string };
          reasons: string[];
        }>;
        pagination: AdminV2CrmDuplicateCandidateListResponse["pagination"];
      },
      { organisationId: string; limit?: number; offset?: number }
    >({
      query: (params) => ({
        url: "/admin/v2/crm/duplicate-identities-report",
        params: optionalQueryParams(params),
      }),
      keepUnusedDataFor: 0,
    }),

    resolveAdminV2SharedAudienceIdentity: builder.mutation<
      {
        contacts: Array<{
          personId: string;
          fullName: string | null;
          primaryEmail: string | null;
        }>;
      },
      { reference: string; occurrence: string }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/identity-review/shared-audience/resolve",
        method: "POST",
        data: body,
      }),
    }),

    getAdminV2CrmIdentityReview: builder.query<
      AdminV2CrmIdentityReviewListResponse,
      {
        q?: string;
        reviewId?: string;
        personId?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/identity-review",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmIdentityReview", id: "LIST" }],
      keepUnusedDataFor: 10,
    }),

    ...buildAdminV2CrmUserEndpoints(builder),
    getAdminV2CrmSupportStateMatrix: builder.query<
      AdminV2CrmSupportStateMatrixResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/crm/support-state-matrix",
      }),
      providesTags: [{ type: "AdminV2CrmSupportStateMatrix", id: "SINGLE" }],
      keepUnusedDataFor: 300,
    }),

    getAdminV2CrmOrganisations: builder.query<
      AdminV2CrmOrganisationsListResponse,
      {
        filters?: string;
        matchLogic?: "all" | "any";
        scopeFilters?: string;
        scopeFilterMatchLogic?: "all" | "any";
        q?: string;
        /** Organisations dashboard only: search what the row shows (row 28). */
        searchScope?: "row";
        /** Dashboard Tags column; other list callers retain the existing shape. */
        includeRecordTags?: true;
        organisationId?: string;
        limit?: number;
        offset?: number;
        sortBy?:
          | "name"
          | "lifetime_value_usd"
          | "active_user_count"
          | "organisation_health"
          | "last_meaningful_activity_at"
          | "data_sync"
          | "last_full_data_refresh_at";
        sortDirection?: "asc" | "desc";
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/organisations",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmOrganisations", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmOrganisationsFilterSuggestions: builder.query<
      AdminV2CrmFilterSuggestionsResponse,
      {
        selectedOwnerIds?: string[];
        selectedTagIds?: string[];
        search?: string;
        filters?: Array<{
          category: string;
          field: string;
          condition: string;
          value: string | string[];
        }>;
        activeClause: {
          category: string;
          field: string;
          condition: string;
          query: string;
        };
        limit?: number;
        organisationId?: string;
        /** Only the Organisations dashboard drawer sends `row`. */
        searchScope?: "row";
      }
    >({
      query: ({ organisationId, searchScope, ...body }) => ({
        url: "/admin/v2/crm/organisations/filter-suggestions",
        method: "POST",
        data: body,
        params: optionalQueryParams({ organisationId, searchScope }),
      }),
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmOrganisationAccountingConnectionRepairPreview: builder.query<
      AdminV2CrmOrganisationAccountingConnectionRepairPreviewResponse,
      { organisationId: string }
    >({
      query: ({ organisationId }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/fix-accounting-connection/preview`,
      }),
      providesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `accounting-connection-fix:${organisationId}`,
        },
      ],
      keepUnusedDataFor: 10,
    }),

    fixAdminV2CrmOrganisationAccountingConnection: builder.mutation<
      AdminV2CrmOrganisationAccountingConnectionRepairResponse,
      {
        organisationId: string;
        body: {
          reason: string;
        };
      }
    >({
      query: ({ organisationId, body }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/fix-accounting-connection`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `organisation:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `accounting-connection-fix:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `connection-history:${organisationId}`,
        },
      ],
    }),

    getAdminV2CrmOrganisationConnectionHistory: builder.query<
      AdminV2CrmOrganisationConnectionHistoryResponse,
      { organisationId: string; limit?: number }
    >({
      query: ({ organisationId, ...params }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/connection-history`,
        params: optionalQueryParams(params),
      }),
      providesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `connection-history:${organisationId}`,
        },
      ],
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmOrganisationDisconnectPreview: builder.query<
      AdminV2CrmOrganisationDisconnectPreviewResponse,
      { organisationId: string }
    >({
      query: ({ organisationId }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/disconnect/preview`,
      }),
      providesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `disconnect-preview:${organisationId}`,
        },
      ],
      keepUnusedDataFor: 10,
    }),

    disconnectAdminV2CrmOrganisation: builder.mutation<
      AdminV2CrmOrganisationDisconnectResponse,
      {
        organisationId: string;
        body: {
          reasonCode:
            "ERR_CURRENCY" | "ERR_ENTITY" | "MIG_LEDGER" | "CLT_REPLACE";
          notes: string;
          sendReconnectEmail: boolean;
          /**
           * When true, the backend writes a Phase-4-consumable
           * `subscriptionTransferPending` audit row so the customer
           * can reconnect a different tenant and inherit their
           * existing subscription. Server validates against the
           * preview's `transferEligibility` and returns 422
           * `transfer_unsupported_subscription_type` when the
           * subscription cannot be moved (e.g. xero-app-store).
           */
          carrySubscriptionForward?: boolean;
          managerOverride?: {
            approvedBy: string;
            justification: string;
          };
        };
      }
    >({
      query: ({ organisationId, body }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/disconnect`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `organisation:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `disconnect-preview:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `connection-history:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `accounting-connection-fix:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `pending-subscription-transfer:${organisationId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmOrganisationPendingSubscriptionTransfer: builder.query<
      AdminV2CrmOrganisationPendingSubscriptionTransferResponse,
      { organisationId: string }
    >({
      query: ({ organisationId }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/subscription-transfer/pending`,
      }),
      providesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `pending-subscription-transfer:${organisationId}`,
        },
      ],
      keepUnusedDataFor: 30,
    }),

    cancelAdminV2CrmOrganisationSubscriptionTransfer: builder.mutation<
      AdminV2CrmOrganisationCancelSubscriptionTransferResponse,
      { organisationId: string; body: { notes: string } }
    >({
      query: ({ organisationId, body }) => ({
        url: `/admin/v2/crm/organisations/${organisationId}/subscription-transfer/cancel`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { organisationId }) => [
        {
          type: "AdminV2CrmUsers",
          id: `pending-subscription-transfer:${organisationId}`,
        },
        {
          type: "AdminV2CrmUsers",
          id: `connection-history:${organisationId}`,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    createAdminV2CrmPerson: builder.mutation<
      {
        person: AdminV2CrmPersonRecordResponse["person"];
        /** R10: "reused" when the email's existing Contact was opened instead. */
        outcome?: "created" | "reused";
        /** Fields a reuse filled (never values); null on a replay. */
        filledFields?: Array<
          "name" | "business" | "phone" | "notes" | "source" | "tags"
        > | null;
      },
      {
        body: {
          /** Idempotency key: the API replays the record for a repeated ID. */
          creationRequestId?: string;
          /** Separate staff choice; a request UUID alone never confirms a duplicate. */
          confirmDistinctContact?: boolean;
          organisationId?: string | null;
          primaryEmail: string;
          /** DEC-13: New contact sends first and (optional) last name. */
          firstName?: string;
          lastName?: string;
          /** The API composes it from first and last name when absent. */
          fullName?: string;
          businessName?: string | null;
          lifecycleStage?: string;
          pipelineStatus?: string | null;
          contactHealthOverride?: string | null;
          contactHealthOverrideReason?: string | null;
          phoneE164?: string | null;
          consentPreferences?: JsonValue;
          tags?: string[];
          notes?: string | null;
          source?: string | null;
        };
      }
    >({
      query: ({ body }) => ({
        url: "/admin/v2/crm/people",
        method: "POST",
        data: body,
      }),
      // R10: a reuse may have filled blank details on that Contact's record.
      invalidatesTags: (result) => [
        { type: "AdminV2CrmPeople", id: "LIST" },
        ...(result?.outcome === "reused" && result.person
          ? [{ type: "AdminV2CrmPeople" as const, id: result.person.id }]
          : []),
      ],
    }),

    getAdminV2CrmStaffUsers: builder.query<
      AdminV2CrmStaffUsersResponse,
      { search?: string; limit?: number } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/staff-users",
        params: {
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.limit ? { limit: params.limit } : {}),
        },
      }),
      keepUnusedDataFor: 60,
    }),

    previewAdminV2CrmPersonMerge: builder.mutation<
      AdminV2CrmPersonMergePreviewResponse,
      {
        primaryPersonId: string;
        duplicatePersonId: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/people/merge-preview",
        method: "POST",
        data: body,
      }),
    }),

    mergeAdminV2CrmPeople: builder.mutation<
      AdminV2CrmPersonMergeResponse,
      {
        primaryPersonId: string;
        duplicatePersonId: string;
        reason: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/people/merge",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2CrmPeople", id: "LIST" },
        { type: "AdminV2CrmDuplicates", id: "LIST" },
        { type: "AdminV2CrmIdentityReview", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    restoreAdminV2CrmPerson: builder.mutation<
      AdminV2CrmPersonRestoreResponse,
      {
        personId: string;
        // PD-130 r2: restore asks for a reason (9 Oct Record Pages PRD).
        body: {
          reason: string;
          releaseConflictingEmails?: boolean;
          recoveryVersion?: string;
        };
      }
    >({
      query: ({ personId, body }) => ({
        url: `/admin/v2/crm/people/${personId}/restore`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: "LIST" },
        { type: "AdminV2CrmPeople", id: personId },
        { type: "AdminV2CrmDuplicates", id: "LIST" },
        { type: "AdminV2CrmIdentityReview", id: "LIST" },
        { type: "AdminV2CrmTimeline", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    archiveAdminV2CrmPerson: builder.mutation<
      AdminV2CrmPersonArchiveResponse,
      {
        personId: string;
        body: {
          reason: string;
        };
      }
    >({
      query: ({ personId, body }) => ({
        url: `/admin/v2/crm/people/${personId}/archive`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: "LIST" },
        { type: "AdminV2CrmPeople", id: personId },
        { type: "AdminV2CrmDuplicates", id: "LIST" },
        { type: "AdminV2CrmIdentityReview", id: "LIST" },
        { type: "AdminV2CrmTimeline", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2CrmIdentityReviewItem: builder.mutation<
      { ok: boolean },
      {
        sourceKind: "identity_issue" | "migration_issue" | "external_ref";
        itemId: string;
        body: {
          action: "resolve" | "ignore";
          note?: string | null;
        };
      }
    >({
      // Identity review needs its own invalidation because duplicate
      // preview and queue actions can mutate this surface independently.
      query: ({ sourceKind, itemId, body }) => ({
        url: `/admin/v2/crm/identity-review/${sourceKind}/${itemId}/actions`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2CrmIdentityReview", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    bulkUpdateAdminV2CrmPeopleLifecycleStage: builder.mutation<
      AdminV2CrmPeopleBulkLifecycleStageResponse,
      {
        organisationId: string;
        lifecycleStage: string;
        personIds: string[];
      }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/people/bulk-actions/lifecycle-stage",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2Jobs", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    assignAdminV2CrmPeopleOwner: builder.mutation<
      AdminV2CrmPeopleOwnerAssignmentResponse,
      {
        personIds: string[];
        ownerStaffUserId: string | null;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/people/bulk-actions/owner",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { personIds }) => [
        { type: "AdminV2CrmPeople", id: "LIST" },
        ...personIds.map((personId) => ({
          type: "AdminV2CrmPeople" as const,
          id: personId,
        })),
        { type: "AdminV2CrmTimeline", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmPersonRecord: builder.query<
      AdminV2CrmPersonRecordResponse,
      /** `linked_pair`: recordTags also carry the linked user's (TAG-03). */
      { personId: string; tagView?: "linked_pair"; readOwner?: string }
    >({
      query: ({ personId, tagView }) => ({
        url: `/admin/v2/crm/people/${personId}`,
        ...(tagView ? { params: { tagView } } : {}),
      }),
      providesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: personId },
      ],
      keepUnusedDataFor: 20,
    }),

    updateAdminV2CrmPerson: builder.mutation<
      {
        person: AdminV2CrmPersonRecordResponse["person"];
        healthUpdateStatus?: "updated" | "skipped_linked_account";
      },
      {
        personId: string;
        body: {
          organisationId?: string;
          primaryEmail?: string;
          phoneE164?: string | null;
          industry?: string | null;
          heardAboutUs?: string | null;
          fullName?: string;
          lifecycleStage?: string;
          pipelineStatus?: string | null;
          contactHealthOverride?: string | null;
          contactHealthOverrideReason?: string | null;
          consentPreferences?: JsonValue;
          tags?: string[];
          notes?: string | null;
        };
      }
    >({
      query: ({ personId, body }) => ({
        url: `/admin/v2/crm/people/${personId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { personId }) => [
        { type: "AdminV2CrmPeople", id: "LIST" },
        { type: "AdminV2CrmPeople", id: personId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmBusinesses: builder.query<
      AdminV2CrmBusinessesListResponse,
      {
        organisationId?: string;
        q?: string;
        filters?: string;
        matchLogic?: "all" | "any";
        limit?: number;
        offset?: number;
        sortBy?: "name" | "updated_at" | "created_at";
        sortDirection?: "asc" | "desc";
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/businesses",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmBusinesses", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmBusinessesFilterSuggestions: builder.query<
      AdminV2CrmFilterSuggestionsResponse,
      {
        selectedOwnerIds?: string[];
        selectedTagIds?: string[];
        search?: string;
        filters?: Array<{
          category: string;
          field: string;
          condition: string;
          value: string | string[];
        }>;
        activeClause: {
          category: string;
          field: string;
          condition: string;
          query: string;
        };
        limit?: number;
        organisationId?: string;
        /** Audience definitions saved from a dashboard may opt into row search. */
        searchScope?: "row";
      }
    >({
      query: ({ organisationId, searchScope, ...body }) => ({
        url: "/admin/v2/crm/businesses/filter-suggestions",
        method: "POST",
        data: body,
        params: optionalQueryParams({ organisationId, searchScope }),
      }),
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmBusinessRecord: builder.query<
      AdminV2CrmBusinessRecordResponse,
      { businessId: string }
    >({
      query: ({ businessId }) => ({
        url: `/admin/v2/crm/businesses/${businessId}`,
      }),
      providesTags: (_result, _error, { businessId }) => [
        { type: "AdminV2CrmBusinesses", id: businessId },
      ],
      keepUnusedDataFor: 20,
    }),

    createAdminV2CrmBusiness: builder.mutation<
      { business: AdminV2CrmBusinessRecordResponse["business"] },
      {
        body: {
          name: string;
          parentOrganisationId?: string | null;
        };
      }
    >({
      query: ({ body }) => ({
        url: "/admin/v2/crm/businesses",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2CrmBusinesses", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2CrmBusiness: builder.mutation<
      { business: AdminV2CrmBusinessRecordResponse["business"] },
      {
        businessId: string;
        body: {
          name?: string;
          parentOrganisationId?: string | null;
        };
      }
    >({
      query: ({ businessId, body }) => ({
        url: `/admin/v2/crm/businesses/${businessId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { businessId }) => [
        { type: "AdminV2CrmBusinesses", id: "LIST" },
        { type: "AdminV2CrmBusinesses", id: businessId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmFirms: builder.query<
      AdminV2CrmFirmsListResponse,
      {
        organisationId?: string;
        audienceOrganisationId?: string;
        audiencePreview?: boolean;
        filters?: string;
        matchLogic?: "all" | "any";
        scopeFilters?: string;
        scopeFilterMatchLogic?: "all" | "any";
        q?: string;
        searchScope?: "row";
        scopeSearch?: string;
        scopeSearchScope?: "row" | "support";
        limit?: number;
        offset?: number;
        sortBy?:
          "name" | "updated_at" | "created_at" | "last_meaningful_activity_at";
        sortDirection?: "asc" | "desc";
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/firms",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmFirms", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmFirmsFilterSuggestions: builder.query<
      AdminV2CrmFilterSuggestionsResponse,
      {
        selectedOwnerIds?: string[];
        selectedTagIds?: string[];
        search?: string;
        filters?: Array<{
          category: string;
          field: string;
          condition: string;
          value: string | string[];
        }>;
        activeClause: {
          category: string;
          field: string;
          condition: string;
          query: string;
        };
        limit?: number;
        organisationId?: string;
        audienceOrganisationId?: string;
        audiencePreview?: boolean;
        scopeSearch?: string;
        scopeSearchScope?: "row" | "support";
        /** Only the Partners dashboard drawer sends `row` (delta row 28). */
        searchScope?: "row";
      }
    >({
      query: ({
        organisationId,
        audienceOrganisationId,
        audiencePreview,
        searchScope,
        ...body
      }) => ({
        url: "/admin/v2/crm/firms/filter-suggestions",
        method: "POST",
        data: body,
        params: optionalQueryParams({
          organisationId,
          audienceOrganisationId,
          audiencePreview,
          searchScope,
        }),
      }),
      keepUnusedDataFor: 20,
    }),

    getAdminV2CrmFirmRecord: builder.query<
      AdminV2CrmFirmRecordResponse,
      { firmId: string }
    >({
      query: ({ firmId }) => ({
        url: `/admin/v2/crm/firms/${firmId}`,
      }),
      providesTags: (_result, _error, { firmId }) => [
        { type: "AdminV2CrmFirms", id: firmId },
      ],
      keepUnusedDataFor: 20,
    }),

    createAdminV2CrmFirm: builder.mutation<
      { firm: AdminV2CrmFirmRecordResponse["firm"] },
      {
        body: {
          name: string;
          defaultOrganisationId?: string | null;
          isTrial?: boolean;
          partnerClientSupportEmailsEnabled?: boolean;
          trialExpiresAt?: string | null;
        };
      }
    >({
      query: ({ body }) => ({
        url: "/admin/v2/crm/firms",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2CrmFirms", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2CrmFirm: builder.mutation<
      { firm: AdminV2CrmFirmRecordResponse["firm"] },
      {
        firmId: string;
        body: {
          name?: string;
          defaultOrganisationId?: string | null;
          isTrial?: boolean;
          partnerClientSupportEmailsEnabled?: boolean;
          trialExpiresAt?: string | null;
        };
      }
    >({
      query: ({ firmId, body }) => ({
        url: `/admin/v2/crm/firms/${firmId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: "AdminV2CrmFirms", id: "LIST" },
        { type: "AdminV2CrmFirms", id: firmId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmFirmClientRelationships: builder.query<
      AdminV2CrmFirmClientRelationshipsListResponse,
      {
        q?: string;
        firmId?: string;
        clientOrganisationId?: string;
        status?: AdminV2CrmRelationshipStatus;
        limit?: number;
        offset?: number;
        sortBy?: "updated_at" | "created_at" | "status";
        sortDirection?: "asc" | "desc";
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/firm-client-relationships",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2CrmFirmClientRelationships", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    createAdminV2CrmFirmClientRelationship: builder.mutation<
      {
        relationship: AdminV2CrmFirmClientRelationshipRecordResponse["relationship"];
      },
      {
        body: {
          firmId: string;
          clientOrganisationId: string;
          status?: AdminV2CrmRelationshipStatus;
          billingResponsibility?: AdminV2CrmBillingResponsibility;
          accountManagerUserId?: string | null;
          notes?: string | null;
          metadata?: JsonValue;
        };
      }
    >({
      query: ({ body }) => ({
        url: "/admin/v2/crm/firm-client-relationships",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { body }) => [
        { type: "AdminV2CrmFirmClientRelationships", id: "LIST" },
        { type: "AdminV2CrmFirms", id: "LIST" },
        { type: "AdminV2CrmFirms", id: body.firmId },
        { type: "AdminV2CrmBusinesses", id: "LIST" },
        { type: "AdminV2CrmBusinesses", id: body.clientOrganisationId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    createAdminV2CrmRecordOnlyPartnerLink: builder.mutation<
      { link: { id: string; firmId: string; clientOrganisationId: string } },
      { body: { firmId: string; clientOrganisationId: string } }
    >({
      query: ({ body }) => ({
        url: "/admin/v2/crm/record-only-partner-links",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { body }) => [
        { type: "AdminV2CrmFirms", id: body.firmId },
        { type: "AdminV2CrmBusinesses", id: body.clientOrganisationId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmFirmClientRelationshipRecord: builder.query<
      AdminV2CrmFirmClientRelationshipRecordResponse,
      { relationshipId: string }
    >({
      query: ({ relationshipId }) => ({
        url: `/admin/v2/crm/firm-client-relationships/${relationshipId}`,
      }),
      providesTags: (_result, _error, { relationshipId }) => [
        {
          type: "AdminV2CrmFirmClientRelationships",
          id: relationshipId,
        },
      ],
      keepUnusedDataFor: 20,
    }),

    updateAdminV2CrmFirmClientRelationship: builder.mutation<
      {
        relationship: AdminV2CrmFirmClientRelationshipRecordResponse["relationship"];
      },
      {
        relationshipId: string;
        body: {
          status?: AdminV2CrmRelationshipStatus;
          billingResponsibility?: AdminV2CrmBillingResponsibility;
          accountManagerUserId?: string | null;
          notes?: string | null;
          metadata?: JsonValue;
          clientEmailPolicy?: "allowed" | "suppressed";
        };
      }
    >({
      query: ({ relationshipId, body }) => ({
        url: `/admin/v2/crm/firm-client-relationships/${relationshipId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { relationshipId }) => [
        { type: "AdminV2CrmFirmClientRelationships", id: "LIST" },
        {
          type: "AdminV2CrmFirmClientRelationships",
          id: relationshipId,
        },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2RecordActivity: builder.query<
      AdminV2RecordActivityResponse,
      AdminV2RecordActivityQuery
    >({
      query: (params) => ({
        url: "/admin/v2/crm/timeline",
        params: { ...params, view: "record_activity" },
      }),
      providesTags: (_result, _error, params) => getRecordActivityTags(params),
      keepUnusedDataFor: 0,
    }),

    getAdminV2CrmTimeline: builder.query<
      AdminV2CrmTimelineResponse,
      {
        aggregateType?: string;
        aggregateId?: string;
        categories?: AdminV2CrmTimelineCategory[];
        organisationId: string;
        limit?: number;
        offset?: number;
        search?: string;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/crm/timeline",
        params: params
          ? {
              ...params,
              ...(params.categories && params.categories.length > 0
                ? { categories: params.categories.join(",") }
                : {}),
            }
          : undefined,
      }),
      providesTags: (_result, _error, params) => [
        {
          type: "AdminV2CrmTimeline",
          id:
            params?.aggregateType && params.aggregateId && params.organisationId
              ? `${params.aggregateType}:${params.aggregateId}:${params.organisationId}`
              : "LIST",
        },
      ],
      keepUnusedDataFor: 10,
    }),

    getAdminV2CrmUserTimeline: builder.query<
      AdminV2CrmTimelineResponse,
      {
        userId: string;
        categories?: AdminV2CrmTimelineCategory[];
        limit?: number;
        offset?: number;
        search?: string;
      }
    >({
      query: ({ userId, ...params }) => ({
        url: `/admin/v2/crm/users/${userId}/timeline`,
        params: {
          ...params,
          ...(params.categories && params.categories.length > 0
            ? { categories: params.categories.join(",") }
            : {}),
        },
      }),
      providesTags: (_result, _error, params) => [
        {
          type: "AdminV2CrmTimeline",
          id: `app_user:${params.userId}:all`,
        },
      ],
      keepUnusedDataFor: 10,
    }),

    createAdminV2CrmImportRun: builder.mutation<
      AdminV2CrmImportRunResponse,
      {
        importType: string;
        dryRun?: boolean;
        sourceFilename?: string;
        source?: string;
        organisationId?: string | null;
        idempotencyKey?: string;
        columnMapping?: {
          primaryEmail?: string;
          fullName?: string;
          lifecycleStage?: string;
          contactHealth?: string;
          tags?: string;
          phoneE164?: string;
          notes?: string;
          organisationId?: string;
        };
        rawCsv: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/crm/import-runs",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2CrmPeople", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2CrmImportRun: builder.query<AdminV2CrmImportRunResponse, string>({
      query: (importRunId) => ({
        url: `/admin/v2/crm/import-runs/${importRunId}`,
      }),
      keepUnusedDataFor: 10,
    }),

    ...buildAdminV2MessagingEndpoints(builder),

    ...buildAdminV2GrowthEndpoints(builder),
    ...buildAdminV2AutomationBindingEndpoints(builder),
    ...buildAdminV2SupportTestResetEndpoints(builder),
    ...buildAdminV2SupportPreviewKeepListEndpoints(builder),
    ...buildAdminV2SupportConversationEndpoints(builder),

    getAdminV2Automations: builder.query<
      AdminV2AutomationsListResponse,
      {
        organisationId?: string;
        status?: AdminV2AutomationStatus;
        automationType?: AdminV2AutomationType;
        configurationState?: AdminV2AutomationConfigurationState;
        rolloutMode?: AdminV2AutomationRolloutMode;
        audienceEntityType?: AdminV2AutomationAudienceEntityType;
        ownerStaffUserId?: string;
        outputChannel?: AdminV2AutomationOutputChannel;
        businessFolder?: string;
        q?: string;
        limit?: number;
        offset?: number;
        suppressAccessDeniedModal?: boolean;
      } | void
    >({
      query: (params) => {
        const { suppressAccessDeniedModal, ...queryParams } = params ?? {};
        return {
          url: "/admin/v2/automations",
          params: optionalQueryParams(params ? queryParams : undefined),
          ...(suppressAccessDeniedModal
            ? { suppressAccessDeniedModal: true }
            : {}),
        };
      },
      providesTags: [{ type: "AdminV2Automations", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2AutomationCatalogue: builder.query<
      AdminV2AutomationCatalogueResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/automations/catalogue",
      }),
      providesTags: [{ type: "AdminV2Automations", id: "CATALOGUE" }],
      keepUnusedDataFor: 300,
    }),

    getAdminV2AutomationGlobalRules: builder.query<
      AdminV2AutomationGlobalRulesResponse,
      { organisationId?: string }
    >({
      query: ({ organisationId }) => ({
        url: "/admin/v2/automations/global-rules",
        params: organisationId ? { organisationId } : undefined,
      }),
      providesTags: [{ type: "AdminV2Automations", id: "GLOBAL_RULES" }],
      keepUnusedDataFor: 20,
    }),

    updateAdminV2AutomationGlobalRules: builder.mutation<
      AdminV2AutomationGlobalRulesResponse,
      {
        dailyNonCriticalEmailCap: number;
        rollingSevenDayNonCriticalEmailCap: number;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/automations/global-rules",
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2Automations", id: "GLOBAL_RULES" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAutomationCustomGlobalCaps: builder.query<
      { rules: CustomEmailCap[] },
      void
    >({
      query: () => ({ url: "/admin/v2/automations/global-rules/custom" }),
      providesTags: [{ type: "AdminV2Automations", id: "CUSTOM_GLOBAL_CAPS" }],
    }),
    createAutomationCustomGlobalCap: builder.mutation<
      { rule: CustomEmailCap },
      CustomEmailCapInput
    >({
      query: (data) => ({
        url: "/admin/v2/automations/global-rules/custom",
        method: "POST",
        data,
      }),
      invalidatesTags: [
        { type: "AdminV2Automations", id: "CUSTOM_GLOBAL_CAPS" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),
    updateAutomationCustomGlobalCap: builder.mutation<
      { rule: CustomEmailCap },
      { id: string; input: CustomEmailCapInput }
    >({
      query: ({ id, input }) => ({
        url: `/admin/v2/automations/global-rules/custom/${encodeURIComponent(id)}`,
        method: "PUT",
        data: input,
      }),
      invalidatesTags: [
        { type: "AdminV2Automations", id: "CUSTOM_GLOBAL_CAPS" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    createAdminV2Automation: builder.mutation<
      AdminV2AutomationDetailResponse,
      {
        organisationId?: string;
        name: string;
        automationKey?: string;
        description?: string;
        graphSchema?: AdminV2AutomationGraphSchema;
        maxRunsPerHour?: number;
        metadata?: AdminV2JsonValue;
      } & AdminV2AutomationFoundationPayload
    >({
      query: (body) => ({
        url: "/admin/v2/automations",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2Automations", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2Automation: builder.query<
      AdminV2AutomationDetailResponse,
      { automationId: string; readOwner?: string }
    >({
      query: ({ automationId }) => ({
        url: `/admin/v2/automations/${automationId}`,
      }),
      providesTags: (_result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: automationId },
      ],
      keepUnusedDataFor: 10,
    }),

    updateAdminV2AutomationDraft: builder.mutation<
      AdminV2AutomationDetailResponse,
      {
        automationId: string;
        body: {
          name?: string;
          automationKey?: string;
          description?: string | null;
          graphSchema?: AdminV2AutomationGraphSchema;
          maxRunsPerHour?: number;
          metadata?: AdminV2JsonValue;
          sharedSegmentAudience?: AdminV2SharedSegmentSelection | null;
        } & AdminV2AutomationFoundationPayload;
      }
    >({
      query: ({ automationId, body }) => ({
        url: `/admin/v2/automations/${automationId}/draft`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: "LIST" },
        { type: "AdminV2Automations", id: automationId },
        { type: "AdminV2Automations", id: `INSTANCES_${automationId}` },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    publishAdminV2Automation: builder.mutation<
      AdminV2AutomationPublishResponse,
      { automationId: string; approved?: boolean }
    >({
      query: ({ automationId, approved }) => ({
        url: `/admin/v2/automations/${automationId}/publish`,
        method: "POST",
        data: {
          ...(approved === true ? { approved: true } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: "LIST" },
        { type: "AdminV2Automations", id: automationId },
        { type: "AdminV2Automations", id: `RUNS_${automationId}` },
        { type: "AdminV2Automations", id: `INSTANCES_${automationId}` },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    archiveAdminV2Automation: builder.mutation<
      AdminV2AutomationDetailResponse,
      { automationId: string }
    >({
      query: ({ automationId }) => ({
        url: `/admin/v2/automations/${automationId}/archive`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: "LIST" },
        { type: "AdminV2Automations", id: automationId },
        { type: "AdminV2Automations", id: `RUNS_${automationId}` },
        { type: "AdminV2Automations", id: `INSTANCES_${automationId}` },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2AutomationRuns: builder.query<
      AdminV2AutomationRunsResponse,
      {
        automationId: string;
        organisationId?: string;
        status?: AdminV2AutomationRunStatus;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ automationId, ...params }) => ({
        url: `/admin/v2/automations/${automationId}/runs`,
        params,
      }),
      providesTags: (result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: `RUNS_${automationId}` },
        ...(result?.runs ?? []).map((run) => ({
          type: "AdminV2Automations" as const,
          id: `RUN_${run.id}`,
        })),
      ],
      keepUnusedDataFor: 10,
    }),

    getAdminV2AutomationWorkflowInstances: builder.query<
      AdminV2AutomationWorkflowInstancesResponse,
      {
        automationId: string;
        organisationId?: string;
        state?: AdminV2AutomationWorkflowInstanceState;
        entityType?: AdminV2AutomationAudienceEntityType;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ automationId, ...params }) => ({
        url: `/admin/v2/automations/${automationId}/instances`,
        params,
      }),
      providesTags: (result, _error, { automationId }) => [
        { type: "AdminV2Automations", id: `INSTANCES_${automationId}` },
        ...(result?.instances ?? []).map((instance) => ({
          type: "AdminV2Automations" as const,
          id: `INSTANCE_${instance.id}`,
        })),
      ],
      keepUnusedDataFor: 10,
    }),

    getAdminV2AutomationRunDetail: builder.query<
      AdminV2AutomationRunDetailResponse,
      { runId: string }
    >({
      query: ({ runId }) => ({
        url: `/admin/v2/automations/runs/${runId}`,
      }),
      providesTags: (_result, _error, { runId }) => [
        { type: "AdminV2Automations", id: `RUN_${runId}` },
      ],
      keepUnusedDataFor: 10,
    }),

    getAdminV2IssueReports: builder.query<
      AdminV2IssueReportsListResponse,
      {
        site?: AdminV2IssueReportSite;
        status?: AdminV2IssueReportStatus | "open";
        statusGroup?: "dev" | "cs" | "archived";
        category?: string;
        excludeCategory?: string;
        priority?: AdminV2IssueReportPriority;
        assignedToStaffUserId?: string;
        reviewedByStaffUserId?: string;
        createdByStaffUserId?: string;
        personStaffUserId?: string;
        organisationId?: string;
        /** B8: one CRM person's reports; refused without organisationId. */
        reporterCrmPersonId?: string;
        route?: string;
        hasComments?: boolean;
        hasUnread?: boolean;
        needsMyRole?: boolean;
        unassignedHighPriority?: boolean;
        updatedSince?: string;
        updatedBefore?: string;
        q?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/issue-reports",
        params: optionalQueryParams(params),
      }),
      providesTags: (result) => {
        if (!result?.issueReports.length) {
          return [{ type: "AdminV2IssueReports", id: "LIST" }];
        }

        return [
          { type: "AdminV2IssueReports", id: "LIST" },
          ...result.issueReports.map((issueReport) => ({
            type: "AdminV2IssueReports" as const,
            id: issueReport.id,
          })),
        ];
      },
      keepUnusedDataFor: 15,
    }),

    // D-T24: an organisation's open feature requests (moved from its issue
    // reports). Moving an issue invalidates the issue-reports list, so this
    // read shares its tag and both feeds refresh together.
    getAdminV2CrmOrganisationFeatureRequests: builder.query<
      AdminV2CrmOrganisationFeatureRequestsResponse,
      { organisationId: string; limit?: number; offset?: number }
    >({
      query: ({ organisationId, ...params }) => ({
        url: `/admin/v2/crm/organisations/${encodeURIComponent(organisationId)}/feature-requests`,
        params: optionalQueryParams(params),
        // D-T24 option A: staff without Support manage (or the organisation
        // grant) are refused; the card leaves the queue out rather than
        // showing the app-wide access modal.
        suppressAccessDeniedModal: true,
      }),
      // Moving an issue invalidates the issue-reports list; archiving or a
      // duplicate review invalidates its Feature Request tags (Codex P2).
      providesTags: (result) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "FeatureRequest", id: "LIST" },
        ...(result?.featureRequests ?? []).map(({ id }) => ({
          type: "FeatureRequest" as const,
          id,
        })),
      ],
      keepUnusedDataFor: 15,
    }),

    getAdminV2IssueReport: builder.query<
      AdminV2IssueReportDetailResponse,
      { issueReportId: string; readOwner?: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}`,
      }),
      providesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
      keepUnusedDataFor: 15,
    }),

    getAdminV2IssueReportBusinessRules: builder.query<
      AdminV2IssueReportBusinessRulesResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/issue-reports/business-rules",
      }),
      providesTags: [{ type: "AdminV2IssueReports", id: "BUSINESS_RULES" }],
      keepUnusedDataFor: 60,
    }),

    updateAdminV2IssueReportBusinessRules: builder.mutation<
      AdminV2IssueReportBusinessRulesResponse,
      { businessRules: string }
    >({
      query: (body) => ({
        url: "/admin/v2/issue-reports/business-rules",
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: [{ type: "AdminV2IssueReports", id: "BUSINESS_RULES" }],
    }),

    generateAdminV2IssueReportBugSuggestions: builder.mutation<
      { bugSuggestionResult: AdminV2IssueReportBugSuggestionResult },
      {
        category: string;
        adminUrl: string;
        routeHint?: string;
        pageTitle?: string;
        clientContext?: AdminV2JsonValue;
        element: AdminV2JsonValue;
        diagnosticsDigest?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/issue-reports/bug-suggestions",
        method: "POST",
        data: body,
      }),
    }),

    createAdminV2IssueReportDraft: builder.mutation<
      { issueReport: AdminV2IssueReport },
      {
        site?: AdminV2IssueReportSite;
        category: string;
        priority?: string;
        tumu?: string | null;
        title: string;
        description?: string;
        reporterSource?: "staff" | "customer";
        reporterStaffUserId?: string;
        reporterCrmPersonId?: string;
        organisationId?: string;
        organisationName?: string;
        adminUrl: string;
        routeHint?: string;
        frontendDiagnostics?: AdminV2JsonValue;
        clientContext?: AdminV2JsonValue;
        elementSelector?: string;
        elementRect?: AdminV2JsonValue;
        elementLabel?: string;
        screenshotPngDataUrl?: string;
        screenshotFileName?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/issue-reports/draft",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "AdminV2IssueReports", id: "LIST" }],
    }),

    validateAdminV2IssueReportDraft: builder.mutation<
      {
        issueReport: AdminV2IssueReport;
        validationResult: AdminV2IssueReportValidationResult;
      },
      { issueReportId: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/validate`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    submitAdminV2IssueReport: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        followUpAnswers: AdminV2IssueReportFollowUpAnswer[];
        additionalContext?: string;
      }
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/submit`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    addAdminV2IssueReportComment: builder.mutation<
      AdminV2IssueReportDetailResponse,
      { issueReportId: string; formData: FormData }
    >({
      query: ({ issueReportId, formData }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/comments`,
        method: "POST",
        data: formData,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    performAdminV2IssueReportAction: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        action: AdminV2IssueReportAction;
        note?: string;
        duplicateReference?: string;
        closeoutScreenshotPngDataUrl?: string;
        closeoutScreenshotFileName?: string;
        createLiveAlert?: boolean;
        liveAlertNote?: string;
        deliveryEvidence?: AdminV2IssueReportDeliveryEvidenceInput;
        uatOwnerStaffUserId?: string;
        closeout?: AdminV2IssueReportCloseoutInput;
      }
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/actions`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    recordAdminV2IssueReportDeliveryMilestone: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        milestone: Extract<
          AdminV2IssueReportDeliveryMilestone,
          "merged" | "preview" | "staging" | "production"
        >;
        occurredAt: string;
        outcome: "passed" | "failed";
      } & AdminV2IssueReportDeliveryEvidenceInput
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/delivery-events`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    recordAdminV2IssueReportUatResult: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        state: "passed" | "failed" | "waived";
        note?: string;
        evidenceUrl?: string;
        correlationId: string;
      }
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/uat-reviews`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    moveAdminV2IssueReportToFeatureRequests: builder.mutation<
      AdminV2IssueReportMoveToFeatureRequestsResponse,
      { issueReportId: string; createLiveAlert: boolean }
    >({
      query: ({ issueReportId, createLiveAlert }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/move-to-feature-requests`,
        method: "POST",
        data: { createLiveAlert },
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2IssueReportMasterLink: builder.query<
      AdminV2IssueReportMasterLinkResponse,
      { issueReportId: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/master-link`,
      }),
      providesTags: (result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: issueReportId },
        ...(result?.master
          ? [
              {
                type: "AdminV2IssueReports" as const,
                id: result.master.id,
              },
            ]
          : []),
        ...(result?.linkedTickets ?? []).map((ticket) => ({
          type: "AdminV2IssueReports" as const,
          id: ticket.id,
        })),
      ],
      keepUnusedDataFor: 15,
    }),

    linkAdminV2IssueReportMasterLink: builder.mutation<
      AdminV2IssueReportMasterLinkMutationResponse,
      { issueReportId: string } & AdminV2IssueReportMasterLinkRequest
    >({
      query: ({ issueReportId, masterIssueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/master-link`,
        method: "POST",
        data: { masterIssueReportId },
      }),
      invalidatesTags: (
        _result,
        _error,
        { issueReportId, masterIssueReportId },
      ) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2IssueReports", id: masterIssueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    unlinkAdminV2IssueReportMasterLink: builder.mutation<
      AdminV2IssueReportMasterLinkMutationResponse,
      { issueReportId: string } & AdminV2IssueReportMasterLinkRequest
    >({
      query: ({ issueReportId, masterIssueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/master-link`,
        method: "DELETE",
        data: { masterIssueReportId },
      }),
      invalidatesTags: (
        _result,
        _error,
        { issueReportId, masterIssueReportId },
      ) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2IssueReports", id: masterIssueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    convertAdminV2IssueReportToBug: builder.mutation<
      AdminV2IssueReportConvertToBugResponse,
      { issueReportId: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/convert-to-bug`,
        method: "POST",
      }),
      invalidatesTags: (result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        ...(result
          ? [
              {
                type: "AdminV2IssueReports" as const,
                id: result.bugIssueReport.id,
              },
            ]
          : []),
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    deleteAdminV2IssueReport: builder.mutation<
      AdminV2IssueReportDeleteResponse,
      { issueReportId: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2IssueReportClassification: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        category?: string;
        priority?: string;
        tumu?: string | null;
        reclassificationProblemOpportunity?: string;
        reclassificationDesiredOutcome?: string;
        reclassificationAdditionalContext?: string;
        createLiveAlert?: boolean;
      }
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/classification`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    updateAdminV2IssueReportSite: builder.mutation<
      AdminV2IssueReportDetailResponse,
      { issueReportId: string; site: AdminV2IssueReportSite }
    >({
      query: ({ issueReportId, site }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/site`,
        method: "PATCH",
        data: { site },
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    updateAdminV2IssueReportIssueText: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        reporterWords?: string | null;
        expectedBehavior?: string;
        actualBehavior?: string;
      }
    >({
      query: ({ issueReportId, ...body }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/issue-text`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    updateAdminV2IssueReportAssignee: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        staffUserId: string | null;
      }
    >({
      query: ({ issueReportId, staffUserId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/assignee`,
        method: "PATCH",
        data: { staffUserId },
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    updateAdminV2IssueReportReviewer: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        staffUserId: string | null;
      }
    >({
      query: ({ issueReportId, staffUserId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/reviewer`,
        method: "PATCH",
        data: { staffUserId },
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    markAdminV2IssueReportRead: builder.mutation<
      AdminV2IssueReportDetailResponse,
      { issueReportId: string }
    >({
      query: ({ issueReportId }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/read`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    updateAdminV2IssueReportWatcher: builder.mutation<
      AdminV2IssueReportDetailResponse,
      {
        issueReportId: string;
        watching: boolean;
      }
    >({
      query: ({ issueReportId, watching }) => ({
        url: `/admin/v2/issue-reports/${issueReportId}/watchers/me`,
        method: "PATCH",
        data: { watching },
      }),
      invalidatesTags: (_result, _error, { issueReportId }) => [
        { type: "AdminV2IssueReports", id: "LIST" },
        { type: "AdminV2IssueReports", id: issueReportId },
      ],
    }),

    getAdminV2ReleaseReviewCurrent: builder.query<
      AdminV2ReleaseReviewCurrentResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/release-review/current",
      }),
      providesTags: (result) => [
        { type: "AdminV2ReleaseReview", id: "CURRENT" },
        ...(result?.review
          ? [
              {
                type: "AdminV2ReleaseReview" as const,
                id: result.review.id,
              },
            ]
          : []),
      ],
      keepUnusedDataFor: 15,
    }),

    getAdminV2ReleaseReviewHistory: builder.query<
      AdminV2ReleaseReviewHistoryResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/release-review/history",
      }),
      providesTags: [{ type: "AdminV2ReleaseReview", id: "HISTORY" }],
      keepUnusedDataFor: 30,
    }),

    getAdminV2ReleaseReview: builder.query<
      AdminV2ReleaseReviewDetailResponse,
      { reviewId: string }
    >({
      query: ({ reviewId }) => ({
        url: `/admin/v2/release-review/${reviewId}`,
      }),
      providesTags: (_result, _error, { reviewId }) => [
        { type: "AdminV2ReleaseReview", id: reviewId },
      ],
      keepUnusedDataFor: 30,
    }),

    refreshAdminV2ReleaseReview: builder.mutation<
      AdminV2ReleaseReviewCurrentResponse,
      void
    >({
      query: () => ({
        url: "/admin/v2/release-review/current/refresh",
        method: "POST",
        data: {},
      }),
      invalidatesTags: [
        { type: "AdminV2ReleaseReview", id: "CURRENT" },
        { type: "AdminV2ReleaseReview", id: "HISTORY" },
      ],
    }),

    rerunAdminV2ReleaseReviewAi: builder.mutation<
      AdminV2ReleaseReviewCurrentResponse,
      { reviewId: string }
    >({
      query: ({ reviewId }) => ({
        url: `/admin/v2/release-review/${reviewId}/recommendations`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { reviewId }) => [
        { type: "AdminV2ReleaseReview", id: "CURRENT" },
        { type: "AdminV2ReleaseReview", id: reviewId },
      ],
    }),

    updateAdminV2ReleaseReviewItem: builder.mutation<
      AdminV2ReleaseReviewDetailResponse,
      {
        reviewId: string;
        itemId: string;
        status?: AdminV2ReleaseReviewItemStatus;
        note?: string | null;
        linkedIssueReportId?: string | null;
      }
    >({
      query: ({ reviewId, itemId, ...body }) => ({
        url: `/admin/v2/release-review/${reviewId}/items/${itemId}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, _error, { reviewId }) => [
        { type: "AdminV2ReleaseReview", id: "CURRENT" },
        { type: "AdminV2ReleaseReview", id: reviewId },
      ],
    }),

    addAdminV2ReleaseReviewComment: builder.mutation<
      AdminV2ReleaseReviewDetailResponse,
      {
        reviewId: string;
        itemId?: string | null;
        body: string;
      }
    >({
      query: ({ reviewId, ...body }) => ({
        url: `/admin/v2/release-review/${reviewId}/comments`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { reviewId }) => [
        { type: "AdminV2ReleaseReview", id: "CURRENT" },
        { type: "AdminV2ReleaseReview", id: reviewId },
      ],
    }),

    getAdminV2HelpCollections: builder.query<
      AdminV2HelpCollectionsListResponse,
      {
        q?: string;
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/support/help-collections",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2HelpCollections", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    createAdminV2HelpCollection: builder.mutation<
      AdminV2HelpCollectionRecordResponse,
      {
        organisationId?: string;
        slug: string;
        name: string;
        description?: string | null;
        sortOrder?: number;
        metadata?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/support/help-collections",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2HelpCollections", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    updateAdminV2HelpCollection: builder.mutation<
      AdminV2HelpCollectionUpdateResponse,
      {
        collectionId: string;
        body: {
          slug?: string;
          name?: string;
          description?: string | null;
          sortOrder?: number;
          isActive?: boolean;
          metadata?: AdminV2JsonValue;
        };
      }
    >({
      query: ({ collectionId, body }) => ({
        url: `/admin/v2/support/help-collections/${collectionId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { collectionId }) => [
        { type: "AdminV2HelpCollections", id: "LIST" },
        { type: "AdminV2HelpCollections", id: collectionId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2HelpArticles: builder.query<
      AdminV2HelpArticlesListResponse,
      {
        q?: string;
        status?: AdminV2HelpArticleStatus;
        collectionId?: string;
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/support/help-articles",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2HelpArticles", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    createAdminV2HelpArticle: builder.mutation<
      AdminV2HelpArticleDetailResponse,
      {
        organisationId?: string;
        collectionId?: string;
        slug: string;
        title: string;
        summary?: string | null;
        bodyMarkdown: string;
        metadata?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/support/help-articles",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2HelpArticles", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminV2HelpArticle: builder.query<
      AdminV2HelpArticleDetailResponse,
      { articleId: string }
    >({
      query: ({ articleId }) => ({
        url: `/admin/v2/support/help-articles/${articleId}`,
      }),
      providesTags: (_result, _error, { articleId }) => [
        { type: "AdminV2HelpArticles", id: articleId },
      ],
      keepUnusedDataFor: 10,
    }),

    updateAdminV2HelpArticle: builder.mutation<
      AdminV2HelpArticleUpdateResponse,
      {
        articleId: string;
        body: {
          slug?: string;
          title?: string;
          summary?: string | null;
          bodyMarkdown?: string;
          collectionId?: string | null;
          metadata?: AdminV2JsonValue;
        };
      }
    >({
      query: ({ articleId, body }) => ({
        url: `/admin/v2/support/help-articles/${articleId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { articleId }) => [
        { type: "AdminV2HelpArticles", id: "LIST" },
        { type: "AdminV2HelpArticles", id: articleId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    publishAdminV2HelpArticle: builder.mutation<
      AdminV2HelpArticleUpdateResponse,
      {
        articleId: string;
      }
    >({
      query: ({ articleId }) => ({
        url: `/admin/v2/support/help-articles/${articleId}/publish`,
        method: "POST",
        data: {},
      }),
      invalidatesTags: (_result, _error, { articleId }) => [
        { type: "AdminV2HelpArticles", id: "LIST" },
        { type: "AdminV2HelpArticles", id: articleId },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    searchAdminV2HelpArticles: builder.mutation<
      AdminV2HelpArticleSearchResponse,
      {
        query: string;
        organisationId?: string;
        limit?: number;
        metadata?: AdminV2JsonValue;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/support/help-search",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "AdminV2HelpSearchLogs", id: "LIST" }],
    }),

    getAdminV2HelpSearchLogs: builder.query<
      AdminV2HelpSearchLogsResponse,
      {
        q?: string;
        source?: string;
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/support/help-search-logs",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2HelpSearchLogs", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    getAdminV2HelpFeedback: builder.query<
      AdminV2HelpFeedbackListResponse,
      {
        articleId?: string;
        isHelpful?: boolean;
        organisationId?: string;
        includeGlobal?: boolean;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (params) => ({
        url: "/admin/v2/support/help-feedback",
        params: optionalQueryParams(params),
      }),
      providesTags: [{ type: "AdminV2HelpFeedback", id: "LIST" }],
      keepUnusedDataFor: 20,
    }),

    createAdminV2HelpFeedback: builder.mutation<
      AdminV2HelpFeedbackRecordResponse,
      {
        articleId: string;
        isHelpful: boolean;
        organisationId?: string;
        comment?: string;
        source?: string;
        metadata?: AdminV2JsonValue;
        submittedByEmail?: string;
        submittedByName?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/v2/support/help-feedback",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [
        { type: "AdminV2HelpFeedback", id: "LIST" },
        { type: "AdminV2AuditLog", id: "LIST" },
      ],
    }),

    getAdminPartners: builder.query<
      AdminPartnerSummary[],
      { search?: string; limit?: number } | void
    >({
      query: (args) => ({
        url: "/admin/partners",
        params: optionalQueryParams(args),
      }),
      keepUnusedDataFor: 60,
    }),
    getAdminPartner: builder.query<AdminPartnerDetail, string>({
      query: (partnerId) => ({
        url: `/admin/partners/${partnerId}`,
      }),
      keepUnusedDataFor: 60,
    }),
    updateAdminPartnerEntitlements: builder.mutation<
      AdminPartnerEntitlementsResponse,
      {
        partnerId: string;
        clientLifetimeLimit?: number;
        clientLimit?: number;
      }
    >({
      query: ({ partnerId, ...body }) => ({
        url: `/admin/partners/${partnerId}/entitlements`,
        method: "POST",
        data: body,
      }),
    }),
    updateAdminPartnerDefaultOrganisation: builder.mutation<
      AdminPartnerDefaultOrganisationResponse,
      { partnerId: string; defaultOrganisationId: string | null }
    >({
      query: ({ partnerId, ...body }) => ({
        url: `/admin/partners/${partnerId}/default-organisation`,
        method: "POST",
        data: body,
      }),
    }),
    unlinkAdminPartnerClient: builder.mutation<
      AdminPartnerUnlinkResponse,
      { partnerId: string; organisationId: string }
    >({
      query: ({ partnerId, organisationId }) => ({
        url: `/admin/partners/${partnerId}/linked/${organisationId}`,
        method: "DELETE",
      }),
    }),

    getIndustryBreakdown: builder.query<IndustryBreakdownItem[], void>({
      query: () => ({
        url: "/admin/industry-breakdown",
      }),
      providesTags: ["SyncData"],
      transformResponse: (res: unknown): IndustryBreakdownItem[] => {
        if (!Array.isArray(res)) {
          return [];
        }

        return res
          .map((candidate) => {
            if (!candidate || typeof candidate !== "object") {
              return null;
            }

            const raw = candidate as Record<string, unknown>;
            const name = typeof raw.name === "string" ? raw.name : null;
            const value = coerceNumber(raw.value);

            if (!name) {
              return null;
            }

            return { name, value };
          })
          .filter((item): item is IndustryBreakdownItem => item !== null);
      },
      keepUnusedDataFor: 300,
    }),

    // Stripe Subscription Management Endpoints

    // Get all Stripe subscriptions
    getStripeSubscriptions: builder.query<
      StripeApiResponse<StripeSubscription>,
      {
        limit?: number;
        starting_after?: string;
        status?: string;
        customer?: string;
      }
    >({
      query: (params) => ({
        url: "/admin/stripe/subscriptions",
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: "StripeSubscription" as const,
                id,
              })),
              { type: "StripeSubscription", id: "LIST" },
            ]
          : [{ type: "StripeSubscription", id: "LIST" }],
      // Subscriptions change frequently
      keepUnusedDataFor: 120, // 2 minutes
    }),

    // Get active Stripe products and prices
    getStripeActiveProducts: builder.query<
      {
        products: StripeApiResponse<StripeProduct>;
        prices: StripeApiResponse<StripePrice>;
      },
      void
    >({
      query: () => ({
        url: "/admin/stripe/activeProducts",
      }),
      providesTags: [
        { type: "StripeProduct", id: "LIST" },
        { type: "StripePrice", id: "LIST" },
      ],
      // Products and prices change less frequently
      keepUnusedDataFor: 600, // 10 minutes
    }),

    // Create checkout session (for testing purposes)
    createCheckoutSession: builder.mutation<
      CheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: (body) => ({
        url: "/admin/stripe/checkout",
        method: "POST",
        data: body,
      }),
      // No cache invalidation needed for checkout sessions
    }),

    createManualCheckoutLink: builder.mutation<
      {
        actionId: string;
        id: string;
        url: string;
        expiresAt: number | null;
        stripeCustomerId: string;
        mode: string;
      },
      {
        actionId: string;
        organisationId: string;
        firebaseUserId: string;
        productId: string;
        successUrl?: string;
        cancelUrl?: string;
        metadata?: Record<string, string>;
      }
    >({
      query: (body) => ({
        url: "/admin/stripe/manual-checkout",
        method: "POST",
        data: body,
      }),
    }),

    // Create customer portal session
    createPortalSession: builder.mutation<
      PortalSessionResponse,
      CreatePortalSessionRequest
    >({
      query: (body) => ({
        url: "/admin/stripe/portal",
        method: "POST",
        data: {
          customer_id: body.customerId,
          subscription_id: body.subscriptionId,
          return_url: body.returnUrl,
        },
      }),
      // No cache invalidation needed for portal sessions
    }),

    // Get subscription statistics for dashboard
    getSubscriptionStatistics: builder.query<
      SubscriptionStatisticsApiResponse,
      void
    >({
      query: () => ({
        url: "/admin/stripe/statistics",
      }),
      providesTags: ["SyncData"],
      // Statistics change less frequently
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // New Product-Grouped Endpoints

    // Get products with grouped prices and primary price configuration
    getProductsWithPrices: builder.query<ProductWithPrices[], void>({
      query: () => ({
        url: "/admin/stripe/products-with-prices",
      }),
      providesTags: [
        { type: "StripeProduct", id: "LIST" },
        { type: "StripePrice", id: "LIST" },
      ],
      // Products and prices change less frequently
      keepUnusedDataFor: 600, // 10 minutes
    }),

    getStripeDiscounts: builder.query<StripeDiscountsResponse, void>({
      query: () => ({
        url: "/admin/stripe/discounts",
      }),
      // Discounts change less frequently; this is primarily for admin UX.
      keepUnusedDataFor: 600, // 10 minutes
    }),

    // Create checkout session using product ID (resolves to primary price)
    createProductCheckoutSession: builder.mutation<
      CheckoutSessionResponse,
      ProductOnboardingRequest
    >({
      query: (body) => ({
        url: "/admin/stripe/product-checkout",
        method: "POST",
        data: body,
      }),
      // No cache invalidation needed for checkout sessions
    }),

    // Get subscription objects for a specific organisation
    getStripeSubscriptionObjects: builder.query<
      StripeSubscriptionObjectsResponse,
      string
    >({
      query: (organisationId) => ({
        url: `/admin/stripe/subscription-objects/${organisationId}`,
      }),
      providesTags: (_result, _error, organisationId) => [
        { type: "StripeSubscriptionObjects", id: organisationId },
      ],
      // Subscription objects change frequently
      keepUnusedDataFor: 120, // 2 minutes
    }),

    // --- Admin subscription tabs (Stripe Audit + Manual Adjustments) ---
    getAdminSubscriptionLocalSnapshot: builder.query<
      AdminSubscriptionLocalSnapshot,
      string
    >({
      query: (orgId) => ({
        url: `/admin/subscriptions/${orgId}/audit/local`,
      }),
      providesTags: (_result, _error, orgId) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
      ],
      keepUnusedDataFor: 60,
    }),

    createAdminSubscriptionAuditRun: builder.mutation<
      CreateAuditRunResponse,
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/admin/subscriptions/${orgId}/audit/runs`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
        { type: "AdminSubscriptionAuditRun", id: orgId },
      ],
    }),

    getAdminSubscriptionLatestAuditRun: builder.query<
      SubscriptionAuditRun | null,
      string
    >({
      query: (orgId) => ({
        url: `/admin/subscriptions/${orgId}/audit/runs/latest`,
      }),
      providesTags: (_result, _error, orgId) => [
        { type: "AdminSubscriptionAuditRun", id: orgId },
      ],
      keepUnusedDataFor: 30,
    }),

    getAdminSubscriptionAuditRun: builder.query<
      SubscriptionAuditRun,
      { orgId: string; runId: string }
    >({
      query: ({ orgId, runId }) => ({
        url: `/admin/subscriptions/${orgId}/audit/runs/${runId}`,
      }),
      providesTags: (_result, _error, { runId }) => [
        { type: "AdminSubscriptionAuditRun", id: runId },
      ],
      keepUnusedDataFor: 0,
    }),

    reconcileAdminSubscriptionFromStripe: builder.mutation<
      ReconcileFromStripeResponse,
      {
        orgId: string;
        reason: string;
        ticketUrl?: string;
        actionId: string;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/admin/subscriptions/${orgId}/audit/repairs/reconcile-from-stripe`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
        { type: "AdminSubscriptionStripeSummary", id: orgId },
      ],
    }),

    rebuildAdminSubscriptionSnapshots: builder.mutation<
      RebuildSnapshotsResponse,
      {
        orgId: string;
        reason: string;
        ticketUrl?: string;
        actionId: string;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/admin/subscriptions/${orgId}/audit/repairs/rebuild-snapshots`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
      ],
    }),

    fixAdminSubscriptionMetadata: builder.mutation<
      ExecuteAdminSubscriptionActionResult,
      {
        orgId: string;
        subscriptionId: string;
        reason: string;
        ticketUrl?: string;
        actionId: string;
        approvalRequestId?: string;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/admin/subscriptions/${orgId}/audit/repairs/fix-subscription-metadata`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
        { type: "AdminSubscriptionStripeSummary", id: orgId },
      ],
    }),

    getAdminSubscriptionStripeSummary: builder.query<
      StripeSubscriptionSummary | null,
      { orgId: string; subscriptionId?: string }
    >({
      query: ({ orgId, subscriptionId }) => ({
        url: `/admin/subscriptions/${orgId}/stripe/summary`,
        params: subscriptionId ? { subscriptionId } : undefined,
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionStripeSummary", id: orgId },
      ],
      keepUnusedDataFor: 0,
    }),

    previewAdminSubscriptionInvoice: builder.mutation<
      StripeInvoicePreviewSummary,
      {
        orgId: string;
        subscriptionId: string;
        newPriceId?: string;
        newQuantity?: number;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/admin/subscriptions/${orgId}/actions/preview-invoice`,
        method: "POST",
        data: body,
      }),
    }),

    listAdminSubscriptionActions: builder.query<
      AdminSubscriptionAction[],
      { orgId: string; limit?: number }
    >({
      query: ({ orgId, limit }) => ({
        url: `/admin/subscriptions/${orgId}/actions`,
        params: limit ? { limit } : undefined,
      }),
      providesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionAction", id: orgId },
      ],
      keepUnusedDataFor: 30,
    }),

    executeAdminSubscriptionAction: builder.mutation<
      ExecuteAdminSubscriptionActionResult,
      {
        orgId: string;
        actionType: string;
        reason: string;
        ticketUrl?: string;
        actionId: string;
        approvalRequestId?: string;
        payload?: JsonValue;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/admin/subscriptions/${orgId}/actions`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { orgId }) => [
        { type: "AdminSubscriptionLocalSnapshot", id: orgId },
        { type: "AdminSubscriptionAction", id: orgId },
        { type: "AdminSubscriptionStripeSummary", id: orgId },
      ],
    }),

    // Get users with Firebase custom claims
    getUsersWithClaims: builder.query<
      AdminUserWithClaimsResponse,
      { firebaseClaimKey: string }
    >({
      query: ({ firebaseClaimKey }) => ({
        url: "/admin/users-with-claims",
        params: { firebaseClaimKey },
      }),
      providesTags: [{ type: "AdminUserWithClaims", id: "LIST" }],
      // Claims data changes less frequently
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Grant free subscription to an organisation
    grantFreeSubscription: builder.mutation<
      {
        success: boolean;
        subscriptionId?: string;
        error?: string;
        details?: {
          organisationId: string;
          stripeCustomerId: string;
          priceId: string;
          couponId: string;
          grantedAt: string;
        };
      },
      {
        organisationId: string;
        reason: string;
        // Required because Sprint 1 billing provenance must record the explicit billed user,
        // not infer one from org membership order.
        subjectFirebaseUserId: string;
        actionId: string;
        duration?: "forever";
      }
    >({
      query: (body) => ({
        url: "/admin/grant-free-subscription",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { organisationId }) => [
        { type: "Organisation", id: organisationId },
        { type: "Organisation", id: "LIST" },
        { type: "StripeSubscriptionObjects", id: organisationId },
      ],
    }),

    // Revoke free subscription
    revokeFreeSubscription: builder.mutation<
      {
        success: boolean;
        error?: string;
      },
      {
        subscriptionId: string;
        reason: string;
        actionId: string;
        organisationId: string;
      }
    >({
      query: (body) => ({
        url: "/admin/revoke-free-subscription",
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { organisationId }) => {
        const tags: Array<{
          type: "Organisation" | "StripeSubscriptionObjects";
          id: string;
        }> = [{ type: "Organisation", id: "LIST" }];

        tags.push({
          type: "StripeSubscriptionObjects",
          id: organisationId,
        });

        return tags;
      },
    }),

    // Remove claim from user
    removeClaimFromUser: builder.mutation<
      {
        success: boolean;
        message?: string;
        error?: string;
      },
      {
        uid: string;
        claimKey: string;
      }
    >({
      query: (body) => ({
        url: "/admin/remove-claim-from-user",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "AdminUserWithClaims", id: "LIST" }],
    }),

    // Disconnect organisation
    disconnectOrganisation: builder.mutation<
      {
        success: boolean;
        message?: string;
        error?: string;
      },
      {
        organisationId: string;
        reason?: string;
      }
    >({
      query: (body) => ({
        url: "/admin/disconnect-organisation",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "Organisation", id: "LIST" }],
    }),

    // Get usage analytics metrics - admin operation
    // @see docs/features/USAGE_TRACKING.md
    getUsageAnalytics: builder.query<
      UsageAnalyticsResponse,
      UsageAnalyticsFilters | void
    >({
      query: (filters = {}) => ({
        url: "/admin/usage-metrics",
        params: filters,
      }),
      providesTags: [{ type: "UsageMetrics", id: "LIST" }],
      keepUnusedDataFor: 60, // Refresh frequently for near-real-time dashboards
    }),

    // Demo Access Management Endpoints

    // Get all demo access assignments
    getDemoAccessAssignments: builder.query<DemoAccessAssignment[], void>({
      query: () => ({
        url: "/admin/demo-access",
      }),
      providesTags: [{ type: "DemoAccess", id: "LIST" }],
      // Demo access assignments change infrequently
      keepUnusedDataFor: 300, // 5 minutes
    }),

    getDemoOpsAccessAssignments: builder.query<DemoAccessAssignment[], void>({
      query: () => ({
        url: "/admin/demo-access",
        params: { view: "demo-ops" },
      }),
      providesTags: [{ type: "DemoAccess", id: "LIST" }],
      // Demo access assignments change infrequently
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Grant demo access to a user
    grantDemoAccess: builder.mutation<
      GrantDemoAccessResponse,
      GrantDemoAccessRequest
    >({
      query: (body) => ({
        url: "/admin/demo-access",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "DemoAccess", id: "LIST" }],
    }),

    // Revoke demo access from a user
    revokeDemoAccess: builder.mutation<
      DemoAccessAssignment,
      RevokeDemoAccessRequest
    >({
      query: ({ firebaseId }) => ({
        url: `/admin/demo-access/${firebaseId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "DemoAccess", id: "LIST" }],
    }),

    // Call Transcript Intake (admin) endpoints
    processTranscriptIntake: builder.mutation<
      TranscriptIntakeProcessResponse,
      TranscriptIntakeProcessRequest
    >({
      query: (body) => ({
        url: "/admin/transcript-intake/process",
        method: "POST",
        data: body,
      }),
    }),

    getTranscriptIntakeJob: builder.query<TranscriptIntakeJobResponse, string>({
      query: (jobId) => ({
        url: `/admin/transcript-intake/jobs/${jobId}`,
      }),
      keepUnusedDataFor: 15,
    }),

    cancelTranscriptIntakeJob: builder.mutation<
      TranscriptIntakeCancelResponse,
      string
    >({
      query: (jobId) => ({
        url: `/admin/transcript-intake/jobs/${jobId}/cancel`,
        method: "POST",
      }),
    }),

    createTranscriptIntakeItems: builder.mutation<
      TranscriptIntakeCreateResponse,
      { jobId: string; body: TranscriptIntakeCreateRequest }
    >({
      query: ({ jobId, body }) => ({
        url: `/admin/transcript-intake/jobs/${jobId}/create`,
        method: "POST",
        data: body,
      }),
    }),

    getAdminDocsSearch: builder.query<
      AdminDocsSearchResponse,
      { query: string; limit?: number; includeArchived?: boolean }
    >({
      query: ({ query, limit, includeArchived }) => ({
        url: "/admin/docs/search",
        params: {
          q: query,
          ...(typeof limit === "number" ? { limit } : {}),
          ...(includeArchived ? { includeArchived: true } : {}),
        },
      }),
      keepUnusedDataFor: 30,
    }),

    createAdminDocsShareLink: builder.mutation<
      CreateDocsShareLinkResponse,
      CreateDocsShareLinkRequest
    >({
      query: (body) => ({
        url: "/admin/docs/share-links",
        method: "POST",
        data: body,
      }),
      invalidatesTags: [{ type: "DocsShareLink", id: "LIST" }],
    }),

    getAdminDocsShareLinks: builder.query<
      AdminDocsShareLinksResponse,
      { status?: DocsShareStatusFilter; limit?: number } | void
    >({
      query: (args) => ({
        url: "/admin/docs/share-links",
        params: {
          ...(args?.status ? { status: args.status } : {}),
          ...(typeof args?.limit === "number" ? { limit: args.limit } : {}),
        },
      }),
      providesTags: (result) =>
        result?.links
          ? [
              { type: "DocsShareLink", id: "LIST" },
              ...result.links.map((link) => ({
                type: "DocsShareLink" as const,
                id: link.id,
              })),
            ]
          : [{ type: "DocsShareLink", id: "LIST" }],
      keepUnusedDataFor: 30,
    }),

    revokeAdminDocsShareLink: builder.mutation<
      RevokeDocsShareLinkResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/admin/docs/share-links/${id}/revoke`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "DocsShareLink", id },
        { type: "DocsShareLink", id: "LIST" },
      ],
    }),
  }),
});

// Export hooks for the consolidated endpoints
export const {
  useGetLogsQuery,
  useGetCashflowDataQualityQuery,
  useGetSpoofSessionsQuery,
  useGetQuickbooksTrialsQuery,
  useGetBulkEmailJobsQuery,
  useGetOrganisationsQuery,
  useRemoveOrganisationUserMutation,
  useGetUsersQuery,
  useGetProviderConnectionOrganisationsQuery,
  useGetDashboardStatisticsQuery,
  useGetAdminMetricsQuery,
  useGetAdminV2MeQuery,
  useUploadAdminV2ProfilePhotoMutation,
  useGetAdminV2LiveAlertsQuery,
  useGetAdminV2LiveAlertsCountQuery,
  useGetAdminV2LiveAlertQuery,
  useGetAdminV2LiveAlertStaffUsersQuery,
  useCreateAdminV2ManualLiveAlertMutation,
  useAddAdminV2LiveAlertNoteMutation,
  useUpdateAdminV2LiveAlertMutation,
  useArchiveAdminV2LiveAlertMutation,
  useRecordAdminV2LiveAlertQuickActionMutation,
  useGetAdminV2AssistantSuggestionsQuery,
  useGetAdminV2AssistantConversationsQuery,
  useCreateAdminV2AssistantConversationMutation,
  useGetAdminV2AssistantMessagesQuery,
  useSendAdminV2AssistantMessageMutation,
  useDeleteAdminV2AssistantConversationMutation,
  useGetAdminV2RevenueAccessContextQuery,
  useGetAdminV2RevenueMrrScoreboardQuery,
  useGetAdminV2RevenueTransactionsQuery,
  useGetAdminV2RevenueTransactionDetailQuery,
  useRefundAdminV2RevenueTransactionMutation,
  useGetAdminV2RevenueSubscriptionsQuery,
  useGetAdminV2RevenueDisputesQuery,
  useGetAdminV2RevenueDisputeDetailQuery,
  useGetAdminV2RevenueWebhookEventsQuery,
  useGetAdminV2RevenueWebhookEventDetailQuery,
  useGetAdminV2RevenueOffersQuery,
  useGetAdminV2RevenueOfferQuery,
  useCreateAdminV2RevenueOfferMutation,
  useUpdateAdminV2RevenueOfferMutation,
  usePublishAdminV2RevenueOfferMutation,
  useGetAdminV2RevenueOnboardingFlowsQuery,
  useGetAdminV2RevenueOnboardingFlowQuery,
  useCreateAdminV2RevenueOnboardingFlowMutation,
  useUpdateAdminV2RevenueOnboardingFlowMutation,
  usePublishAdminV2RevenueOnboardingFlowMutation,
  useGetAdminV2RevenueCouponsQuery,
  useUpsertAdminV2RevenueCouponMutation,
  useGetAdminV2RevenueAffiliatesQuery,
  useUpsertAdminV2RevenueAffiliateMutation,
  useCreateAdminV2RevenueAffiliateAttributionMutation,
  useCreateAdminV2RevenueAffiliatePayoutMutation,
  useMarkPaidAdminV2RevenueAffiliatePayoutMutation,
  useGetAdminV2LearningContentsQuery,
  useGetAdminV2LearningPathwaysQuery,
  useGetAdminV2LearningAssignmentsQuery,
  useGetAdminV2LearningProgressQuery,
  useGetAdminV2LearningScoreRuleVersionsQuery,
  useGetAdminV2LearningPeopleScoreDashboardQuery,
  useGetAdminV2LearningBusinessScoreDashboardQuery,
  useGetAdminV2SalesDealsQuery,
  useCreateAdminV2SalesDealMutation,
  useUpdateAdminV2SalesDealStageMutation,
  useClaimAdminV2SalesDealMutation,
  useGetAdminV2SalesDealEventsQuery,
  useGetAdminV2SalesLeadScoresQuery,
  useGetAdminV2SalesMemberScoresQuery,
  useRecomputeAdminV2SalesScoresMutation,
  useGetAdminV2SalesWorkQueuesQuery,
  useGetAdminV2SalesReportingSummaryQuery,
  useGetAdminV2IntegrationsQuery,
  usePatchAdminV2IntegrationConnectionMutation,
  useGetAdminV2SageBetaInvitesQuery,
  useCreateAdminV2SageBetaInviteMutation,
  useRevokeAdminV2SageBetaInviteMutation,
  useReissueAdminV2SageBetaInviteMutation,
  useGetAdminV2PosthogDashboardsQuery,
  useGetAdminV2EventTaxonomyQuery,
  useGetAdminV2OnboardingFunnelQuery,
  useGetAdminV2ActivationReportQuery,
  useGetAdminV2AnalyticsPipelineHealthQuery,
  useGetAdminV2InteractionElementsQuery,
  useGetAdminV2InteractionHeatmapGridQuery,
  useGetAdminV2InteractionPipelineHealthQuery,
  useGetAdminV2InteractionUnknownElementsQuery,
  useGetAdminV2AiInsightsQualityQuery,
  usePatchAdminV2EventTaxonomyMutation,
  useGetAdminV2OpsWhatNeedsAttentionQuery,
  useGetAdminV2OpsIngestFailuresQuery,
  useGetAdminV2OpsDeliverabilityHealthQuery,
  useGetAdminV2OpsSupportBacklogQuery,
  useGetAdminV2PlatformStatusQuery,
  useGetAdminV2IncidentOrganisationsQuery,
  useGetAdminV2OrgSyncSearchQuery,
  useGetAdminV2EntitlementsQuery,
  useGetAdminV2FeatureAccessOrganisationsQuery,
  useGetAdminV2FeatureAccessQuery,
  useGetAdminV2FeatureReadinessQuery,
  useSetAdminV2FeatureReadinessMutation,
  useUpsertAdminV2EntitlementMutation,
  useDeleteAdminV2EntitlementMutation,
  useGetAdminV2RbacStaffUsersQuery,
  useGetAdminV2RbacRolesQuery,
  useGetAdminV2RbacPermissionsQuery,
  useAssignAdminV2StaffRoleMutation,
  useSetAdminV2StaffPermissionOverrideMutation,
  useLazySearchAdminV2Query,
  useGetAdminV2ApprovalsQuery,
  useCreateAdminV2ApprovalMutation,
  useApproveAdminV2ApprovalMutation,
  useRejectAdminV2ApprovalMutation,
  useCancelAdminV2ApprovalMutation,
  useGetAdminV2AuditLogQuery,
  useGetAdminV2JobsQuery,
  useGetAdminV2JobQuery,
  useCancelAdminV2JobMutation,
  useGetAdminV2CrmPeopleQuery,
  useGetAdminV2CrmPeopleFilterSuggestionsQuery,
  useGetAdminV2CrmDuplicateCandidatesQuery,
  useLazyGetAdminV2CrmDuplicateIdentitiesReportQuery,
  useGetAdminV2CrmIdentityReviewQuery,
  useResolveAdminV2SharedAudienceIdentityMutation,
  useGetAdminV2CrmUsersQuery,
  useGetAdminV2CrmUserRecordQuery,
  useGetAdminV2CrmUsersFilterSuggestionsQuery,
  useRequestAdminV2CrmUserStatusChangeMutation,
  useUpdateAdminV2CrmUserStatusMutation,
  useUpdateAdminV2CrmUserContactDetailsMutation,
  useDisableAdminV2CrmUserMutation,
  useRestoreAdminV2CrmUserMutation,
  useCreateAdminV2CrmUserInternalNoteMutation,
  useGetAdminV2CrmUserInternalNotesQuery,
  useCreateAdminV2CrmRecordInternalNoteMutation,
  useGetAdminV2CrmRecordInternalNotesQuery,
  useCombineAdminV2CrmSignInSplitMutation,
  useGetAdminV2CrmSignInSplitSeparationQuery,
  useSeparateAdminV2CrmSignInSplitMutation,
  useGetAdminV2CrmSupportStateMatrixQuery,
  useGetAdminV2CrmOrganisationsQuery,
  useGetAdminV2CrmOrganisationsFilterSuggestionsQuery,
  useGetAdminV2CrmUserRelationshipRecordQuery,
  useLazyGetAdminV2CrmUserRelationshipRecordQuery,
  useGetAdminV2CrmOrganisationRecordQuery,
  useGetAdminV2CrmOrganisationAccountingConnectionRepairPreviewQuery,
  useGetAdminV2CrmOrganisationContactCandidatesQuery,
  useUpdateAdminV2CrmOrganisationContactRolesMutation,
  useFixAdminV2CrmOrganisationAccountingConnectionMutation,
  useGetAdminV2CrmOrganisationConnectionHistoryQuery,
  useGetAdminV2CrmOrganisationDisconnectPreviewQuery,
  useDisconnectAdminV2CrmOrganisationMutation,
  useGetAdminV2CrmOrganisationPendingSubscriptionTransferQuery,
  useCancelAdminV2CrmOrganisationSubscriptionTransferMutation,
  useCreateAdminV2CrmPersonMutation,
  useGetAdminV2CrmStaffUsersQuery,
  usePreviewAdminV2CrmPersonMergeMutation,
  useMergeAdminV2CrmPeopleMutation,
  useRestoreAdminV2CrmPersonMutation,
  useArchiveAdminV2CrmPersonMutation,
  useUpdateAdminV2CrmIdentityReviewItemMutation,
  useBulkUpdateAdminV2CrmPeopleLifecycleStageMutation,
  useAssignAdminV2CrmPeopleOwnerMutation,
  useGetAdminV2CrmPersonRecordQuery,
  useUpdateAdminV2CrmPersonMutation,
  useGetAdminV2CrmBusinessesQuery,
  useGetAdminV2CrmBusinessesFilterSuggestionsQuery,
  useGetAdminV2CrmBusinessRecordQuery,
  useCreateAdminV2CrmBusinessMutation,
  useUpdateAdminV2CrmBusinessMutation,
  useGetAdminV2CrmFirmsQuery,
  useGetAdminV2CrmFirmsFilterSuggestionsQuery,
  useGetAdminV2CrmFirmRecordQuery,
  useCreateAdminV2CrmFirmMutation,
  useUpdateAdminV2CrmFirmMutation,
  useGetAdminV2CrmFirmClientRelationshipsQuery,
  useCreateAdminV2CrmFirmClientRelationshipMutation,
  useCreateAdminV2CrmRecordOnlyPartnerLinkMutation,
  useGetAdminV2CrmFirmClientRelationshipRecordQuery,
  useUpdateAdminV2CrmFirmClientRelationshipMutation,
  useGetAdminV2RecordActivityQuery,
  useGetAdminV2CrmTimelineQuery,
  useGetAdminV2CrmUserTimelineQuery,
  useCreateAdminV2CrmImportRunMutation,
  useGetAdminV2CrmImportRunQuery,
  useGetAdminV2MessagingSenderIdentitiesQuery,
  useGetAdminV2MessagingSenderIdentityQuery,
  useGetAdminV2MessagingAutomationReviewContextQuery,
  useGetAdminV2MessagingSettingsReadinessQuery,
  useUpdateAdminV2MessagingGlobalEmailDesignMutation,
  useCreateAdminV2MessagingSenderIdentityMutation,
  useUpdateAdminV2MessagingSenderIdentityMutation,
  useArchiveAdminV2MessagingSenderIdentityMutation,
  useGetAdminV2MessagingTemplatesQuery,
  useGetAdminV2MessagingTemplateQuery,
  useLazyGetAdminV2MessagingTemplateQuery,
  useCreateAdminV2MessagingTemplateMutation,
  useUpdateAdminV2MessagingTemplateMutation,
  usePublishAdminV2MessagingTemplateMutation,
  useUnpublishAdminV2MessagingTemplateMutation,
  useArchiveAdminV2MessagingTemplateMutation,
  useRestoreAdminV2MessagingTemplateMutation,
  useCreateAdminV2MessagingTemplateImageUploadMutation,
  useCreateAdminV2MessagingMergeTagRequestMutation,
  usePreviewAdminV2MessagingTemplateMutation,
  usePreviewAdminV2MessagingTemplateHtmlImportMutation,
  usePreviewAdminV2MessagingTemplateSelfTestMutation,
  useSendAdminV2MessagingMessageMutation,
  useReconcileAdminV2MessagingMessageMutation,
  useSendAdminV2MessagingTemplateSelfTestMutation,
  useCreateAdminV2MessagingManualEmailJobMutation,
  useSaveAdminV2MessagingManualEmailJobDraftMutation,
  usePreviewAdminV2MessagingManualEmailJobQuery,
  useGetAdminV2MessagingManualEmailJobContextQuery,
  useGetAdminV2MessagingPersonPreferencesQuery,
  useUpdateAdminV2MessagingPersonPreferenceMutation,
  useGetAdminV2MessagingEmailAssistantContextQuery,
  useGetAdminV2MessagingPersonEmailMessagesQuery,
  useGetAdminV2MessagingEmailActivityQuery,
  useLazyGetAdminV2MessagingBroadcastRecipientsQuery,
  useLazyGetAdminV2MessagingEmailActivityDetailQuery,
  useLazyGetAdminV2MessagingEmailActivityHistoryContactCandidatesQuery,
  useLinkAdminV2MessagingEmailActivityHistoryContactMutation,
  useCreateAdminV2MessagingEmailActivityAttachmentDownloadLinkMutation,
  useGetAdminV2MessagingPersonInAppMessagesQuery,
  useGetAdminV2GrowthFormsQuery,
  useGetAdminV2FormFieldLibraryQuery,
  useCreateAdminV2FormFieldLibraryCustomFieldMutation,
  useUpdateAdminV2FormFieldLibraryCustomFieldMutation,
  useArchiveAdminV2FormFieldLibraryCustomFieldMutation,
  useCreateAdminV2GrowthFormMutation,
  useGetAdminV2GrowthFormQuery,
  useUpdateAdminV2GrowthFormMutation,
  useDuplicateAdminV2GrowthFormMutation,
  usePublishAdminV2GrowthFormMutation,
  useUnpublishAdminV2GrowthFormMutation,
  useArchiveAdminV2GrowthFormMutation,
  useGetAdminV2GrowthFormOrganisationOptionsQuery,
  useAssignAdminV2GrowthFormSubmissionOrganisationMutation,
  useGetAdminV2GrowthFormSubmissionsQuery,
  useReviewAdminV2GrowthFormSubmissionCrmReviewMutation,
  useExportAdminV2GrowthFormSubmissionsMutation,
  useGetAdminV2GrowthTagsQuery,
  useCreateAdminV2GrowthTagMutation,
  useUpdateAdminV2GrowthTagMutation,
  useDeleteAdminV2GrowthTagMutation,
  usePreviewAdminV2GrowthTagBulkMutation,
  useBulkApplyAdminV2GrowthTagMutation,
  useBulkRemoveAdminV2GrowthTagMutation,
  useGetAdminV2GrowthSegmentsQuery,
  useGetAdminV2GrowthOrganisationOptionsQuery,
  useCreateAdminV2GrowthSegmentMutation,
  useUpdateAdminV2GrowthSegmentMutation,
  usePublishAdminV2GrowthSegmentMutation,
  useArchiveAdminV2GrowthSegmentMutation,
  useReactivateAdminV2GrowthSegmentMutation,
  useDemoteAdminV2GrowthSegmentMutation,
  usePreviewAdminV2GrowthSegmentByIdMutation,
  usePreviewAdminV2GrowthSegmentRulesMutation,
  useGetAdminV2GrowthSegmentRecipientsQuery,
  useRecomputeAdminV2GrowthSegmentMutation,
  useGetAdminV2GrowthCampaignsQuery,
  useCreateAdminV2GrowthCampaignMutation,
  useGetAdminV2GrowthCampaignQuery,
  useUpdateAdminV2GrowthCampaignMutation,
  useScheduleAdminV2GrowthCampaignMutation,
  useSendNowAdminV2GrowthCampaignMutation,
  useReviewAdminV2GrowthCampaignAudienceMutation,
  useAttestAdminV2GrowthCampaignProductRelevanceMutation,
  usePreviewAdminV2GrowthCampaignContentMutation,
  usePreviewAdminV2GrowthCampaignContentTestMutation,
  useSendAdminV2GrowthCampaignContentTestMutation,
  useGetAdminV2GrowthSequencePersonEnrollmentsQuery,
  useGetAdminV2AutomationCatalogueQuery,
  useGetAdminV2AutomationGlobalRulesQuery,
  useGetAutomationCustomGlobalCapsQuery,
  useCreateAutomationCustomGlobalCapMutation,
  useUpdateAutomationCustomGlobalCapMutation,
  useUpdateAdminV2AutomationGlobalRulesMutation,
  useGetAdminV2AutomationTenantBindingsQuery,
  useUpdateAdminV2AutomationTenantBindingsMutation,
  useTransitionAdminV2AutomationTenantBindingsMutation,
  useGetAdminV2SupportTestResetCapabilityQuery,
  useLazyGetAdminV2SupportTestResetLookupQuery,
  useGetAdminV2SupportTestResetPreviewQuery,
  useGetAdminV2SupportTestResetPurgeRunQuery,
  usePurgeAdminV2SupportTestResetMutation,
  useGetAdminV2SupportPreviewKeepListCapabilityQuery,
  useGetAdminV2SupportConversationRequestersQuery,
  useCreateAdminV2SupportConversationMutation,
  useGetAdminV2SupportConversationsQuery,
  useGetAdminV2SupportSearchCountQuery,
  useGetAdminV2LinkedSupportConversationsQuery,
  useGetAdminV2SupportConversationQuery,
  useUpdateAdminV2SupportConversationStatusMutation,
  useAssignAdminV2SupportConversationMutation,
  useAddAdminV2SupportConversationNoteMutation,
  useReplyToAdminV2SupportConversationMutation,
  useEscalateAdminV2SupportConversationMutation,
  useGetAdminV2SupportPreviewKeepListQuery,
  useAddAdminV2SupportPreviewKeepListEntryMutation,
  useRemoveAdminV2SupportPreviewKeepListEntryMutation,
  useSnapshotAdminV2SupportPreviewKeepListMutation,
  useRestoreAdminV2SupportPreviewKeepListMutation,
  useGetAdminV2AutomationsQuery,
  useCreateAdminV2AutomationMutation,
  useGetAdminV2AutomationQuery,
  useUpdateAdminV2AutomationDraftMutation,
  usePublishAdminV2AutomationMutation,
  useArchiveAdminV2AutomationMutation,
  useGetAdminV2AutomationRunsQuery,
  useGetAdminV2AutomationWorkflowInstancesQuery,
  useGetAdminV2AutomationRunDetailQuery,
  useGetAdminV2IssueReportsQuery,
  useGetAdminV2CrmOrganisationFeatureRequestsQuery,
  useGetAdminV2IssueReportQuery,
  useGetAdminV2IssueReportBusinessRulesQuery,
  useUpdateAdminV2IssueReportBusinessRulesMutation,
  useGenerateAdminV2IssueReportBugSuggestionsMutation,
  useCreateAdminV2IssueReportDraftMutation,
  useValidateAdminV2IssueReportDraftMutation,
  useSubmitAdminV2IssueReportMutation,
  useAddAdminV2IssueReportCommentMutation,
  usePerformAdminV2IssueReportActionMutation,
  useRecordAdminV2IssueReportUatResultMutation,
  useMoveAdminV2IssueReportToFeatureRequestsMutation,
  useGetAdminV2IssueReportMasterLinkQuery,
  useLinkAdminV2IssueReportMasterLinkMutation,
  useUnlinkAdminV2IssueReportMasterLinkMutation,
  useConvertAdminV2IssueReportToBugMutation,
  useDeleteAdminV2IssueReportMutation,
  useUpdateAdminV2IssueReportClassificationMutation,
  useUpdateAdminV2IssueReportSiteMutation,
  useUpdateAdminV2IssueReportIssueTextMutation,
  useUpdateAdminV2IssueReportAssigneeMutation,
  useUpdateAdminV2IssueReportReviewerMutation,
  useMarkAdminV2IssueReportReadMutation,
  useUpdateAdminV2IssueReportWatcherMutation,
  useGetAdminV2ReleaseReviewCurrentQuery,
  useGetAdminV2OpenAiUsageQuery,
  useGetAdminV2ReleaseReviewHistoryQuery,
  useRefreshAdminV2ReleaseReviewMutation,
  useRerunAdminV2ReleaseReviewAiMutation,
  useUpdateAdminV2ReleaseReviewItemMutation,
  useAddAdminV2ReleaseReviewCommentMutation,
  useGetAdminV2HelpCollectionsQuery,
  useCreateAdminV2HelpCollectionMutation,
  useGetAdminV2HelpArticlesQuery,
  useCreateAdminV2HelpArticleMutation,
  useGetAdminV2HelpArticleQuery,
  useUpdateAdminV2HelpArticleMutation,
  usePublishAdminV2HelpArticleMutation,
  useSearchAdminV2HelpArticlesMutation,
  useGetAdminV2HelpSearchLogsQuery,
  useGetAdminV2HelpFeedbackQuery,
  useGetAdminPartnersQuery,
  useGetAdminPartnerQuery,
  useUpdateAdminPartnerEntitlementsMutation,
  useUpdateAdminPartnerDefaultOrganisationMutation,
  useUnlinkAdminPartnerClientMutation,
  useGetIndustryBreakdownQuery,
  // Stripe subscription management hooks
  useGetStripeSubscriptionsQuery,
  useGetStripeActiveProductsQuery,
  useCreateCheckoutSessionMutation,
  useCreateManualCheckoutLinkMutation,
  useCreatePortalSessionMutation,
  useGetSubscriptionStatisticsQuery,
  // New product-grouped hooks
  useGetProductsWithPricesQuery,
  useGetStripeDiscountsQuery,
  useCreateProductCheckoutSessionMutation,
  // Subscription objects hook
  useGetStripeSubscriptionObjectsQuery,
  // Grant and revoke free subscription hooks
  useGrantFreeSubscriptionMutation,
  useRevokeFreeSubscriptionMutation,
  // Admin subscription audit + manual adjustments hooks
  useGetAdminSubscriptionLocalSnapshotQuery,
  useCreateAdminSubscriptionAuditRunMutation,
  useGetAdminSubscriptionLatestAuditRunQuery,
  useGetAdminSubscriptionAuditRunQuery,
  useReconcileAdminSubscriptionFromStripeMutation,
  useRebuildAdminSubscriptionSnapshotsMutation,
  useFixAdminSubscriptionMetadataMutation,
  useLazyGetAdminSubscriptionStripeSummaryQuery,
  usePreviewAdminSubscriptionInvoiceMutation,
  useListAdminSubscriptionActionsQuery,
  useExecuteAdminSubscriptionActionMutation,
  // Admin users with claims hook
  useGetUsersWithClaimsQuery,
  // Remove claim from user hook
  useRemoveClaimFromUserMutation,
  // Disconnect organisation hook
  useDisconnectOrganisationMutation,
  // Usage analytics hook
  useGetUsageAnalyticsQuery,
  // Demo access management hooks
  useGetDemoAccessAssignmentsQuery,
  useGetDemoOpsAccessAssignmentsQuery,
  useGrantDemoAccessMutation,
  useRevokeDemoAccessMutation,
  useProcessTranscriptIntakeMutation,
  useGetTranscriptIntakeJobQuery,
  useCancelTranscriptIntakeJobMutation,
  useGetAdminDocsSearchQuery,
  useCreateAdminDocsShareLinkMutation,
  useGetAdminDocsShareLinksQuery,
  useRevokeAdminDocsShareLinkMutation,
} = apiSlice;
